import React, { useState, useEffect, useRef, useCallback, useTransition } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../api/homeapi";
import QRCodeLib from "qrcode";
import { downloadTicketsPdf as downloadTicketsPdfAgent } from "../utils/ticketPdf";
import { TrainIcon, GridIcon, CalendarIcon, TicketIcon, CameraIcon, MessageIcon, UsersIcon, MoneyIcon, SettingsIcon, ShieldIcon, LogoutIcon, SeatIcon2, StarIcon, PhoneIcon, PlusIcon, TrashIcon, EditIcon, XIcon, RefreshIcon, PrinterIcon, SearchIcon, AlertIcon, ClockIcon } from "../components/Icons";
import { useSettings } from "../context/SettingsContext";

/* Petit QR code canvas - aperçu billet agent */
export const AgentTicketQR = ({ data, size = 88 }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current || !data) return;
    QRCodeLib.toCanvas(ref.current, data, { width: size, color: { dark: "#1a3d2b", light: "#ffffff" }, errorCorrectionLevel: "H" }).catch(() => {});
  }, [data, size]);
  return <canvas ref={ref} width={size} height={size} style={{ borderRadius: 8, border: "2px solid #1a3d2b" }} />;
};

/* ─────────────────────────── DESIGN TOKENS ─────────────────────────── */
export const T = {
  forest:    "#1a3d2b",
  forestMid: "#2d5a3f",
  forestDk:  "#0f2619",
  gold:      "#c8942a",
  goldLt:    "#e8b84b",
  cream:     "#f5f0e8",
  creamDk:   "#ede6d6",
  rust:      "#8b3a1a",
  ink:       "#0e1f17",
  smoke:     "#8a9e93",
  white:     "#fafaf7",
  blue:      "#1b3f6e",
};

/* ─────────────────────────── CONSTANTS ─────────────────────────── */
export const STATUS = {
  CONFIRMED: { bg: "#d1fae5", color: "#065f46", dot: "#22a76f", label: "Confirmé" },
  PENDING:   { bg: "#fef3c7", color: "#92400e", dot: "#c8942a", label: "En attente" },
  CANCELLED: { bg: "#fee2e2", color: "#991b1b", dot: "#8b3a1a", label: "Annulé" },
};

export const CLASS_MAP = {
  ECONOMIQUE: { icon: <SeatIcon2 size={14}/>, label: "Économique" },
  PREMIERE:   { icon: <StarIcon size={14}/>,  label: "1ère Classe" },
  VIP:        { icon: <ShieldIcon size={14}/>,label: "VIP" },
  DEUXIEME:   { icon: <SeatIcon2 size={14}/>, label: "2ème Classe" },
};

export const TRAVEL_CLASSES = ["ECONOMIQUE", "DEUXIEME", "PREMIERE", "VIP"];

export const STATIONS = [
  "Libreville","Owendo","Ntoum","Oyan","Abanga","Bifoun","Lambaréné",
  "Makouké","Booué","Lastoursville","Moanda","Franceville","Lopé","Ayem","Ivindo",
];

export const OPERATORS_ALL = [
  { id: "AIRTEL_MONEY", icon: <PhoneIcon size={16}/>, name: "Airtel Money",
    steps: ["Composez *555# sur votre Airtel","Choisissez « Paiement marchand »","Code marchand : SETRAG","Saisissez le montant exact","Entrez votre code PIN","Notez la référence SMS"] },
  { id: "MOOV_MONEY",  icon: <PhoneIcon size={16}/>, name: "Moov Money",
    steps: ["Composez *155# sur votre Moov","Choisissez « Payer une facture »","Code biller : SETRAG","Saisissez le montant exact","Entrez votre code PIN","Notez la référence SMS"] },
  { id: "CASH",        icon: <MoneyIcon size={16}/>, name: "Espèces (Guichet)",
    steps: ["Le passager remet les espèces à l'agent","Vérifiez le montant exact","Confirmez dans le système","Remettez le billet imprimé"] },
];

export const ALL_SEATS = Array.from({ length: 80 }, (_, i) => i + 1);

export const fmtPrice = n => Number(n || 0).toLocaleString("fr-FR") + " FCFA";
export const initials = name => (name || "?").split(" ").map(w => w[0]).slice(0,2).join("").toUpperCase();

/* ── Vérifie si un trajet est encore valide (pas encore passé) ── */
export const isTripValid = (trip) => {
  try {
    const dateStr = trip.date;
    const timeStr = trip.departureTime;
    if (!dateStr) return true; // pas de date = on l'affiche quand même
    const dt = new Date(`${dateStr}T${timeStr || "00:00"}:00`);
    return dt > new Date();
  } catch {
    return true;
  }
};

/* ─────────────────────────── CSS ─────────────────────────── */
export const css = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');

*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
html{scroll-behavior:smooth;}

body{
  font-family:'DM Sans',sans-serif;
  background:#eee8da;
  color:${T.ink};
  min-height:100vh;
}
body::before{
  content:'';position:fixed;inset:0;
  background:radial-gradient(circle at 10% 10%,rgba(26,61,43,.07) 0%,transparent 55%),
             radial-gradient(circle at 90% 90%,rgba(200,148,42,.05) 0%,transparent 55%);
  pointer-events:none;z-index:0;
}

.app{position:relative;z-index:1;display:flex;flex-direction:column;min-height:100vh;}
.layout{display:flex;flex:1;min-height:0;}

/* ── SIDEBAR ── */
.sidebar{
  width:236px;flex-shrink:0;
  background:${T.forestDk};
  display:flex;flex-direction:column;
  position:sticky;top:0;height:100vh;overflow-y:auto;
  transition:transform .3s ease;
  z-index:200;
}
.sb-logo{
  padding:22px 18px 18px;
  border-bottom:1px solid rgba(255,255,255,.07);
  display:flex;align-items:center;gap:11px;
}
.sb-logo-icon{
  width:38px;height:38px;display:flex;align-items:center;justify-content:center;flex-shrink:0;color:${T.white};
}
.sb-logo-name{font-family:'Playfair Display',serif;font-size:16px;font-weight:900;color:${T.white};letter-spacing:.03em;}
.sb-logo-sub{font-size:9px;color:rgba(255,255,255,.35);text-transform:uppercase;letter-spacing:.11em;margin-top:1px;}

.sb-nav{padding:10px 10px;flex:1;}
.sb-section-lbl{font-size:9px;font-weight:700;color:rgba(255,255,255,.25);text-transform:uppercase;letter-spacing:.13em;padding:10px 12px 4px;}
.sb-item{
  display:flex;align-items:center;gap:9px;
  padding:9px 12px;border-radius:9px;cursor:pointer;
  font-size:13px;font-weight:500;color:rgba(255,255,255,.55);
  transition:all .17s;margin-bottom:1px;
  border:none;background:transparent;width:100%;text-align:left;
  font-family:'DM Sans',sans-serif;
}
.sb-item:hover{background:rgba(255,255,255,.07);color:${T.white};}
.sb-item.active{background:rgba(200,148,42,.15);color:${T.goldLt};font-weight:700;}
.sb-item-icon{font-size:15px;flex-shrink:0;opacity:.75;}
.sb-item.active .sb-item-icon{opacity:1;}

.sb-footer{padding:14px 10px;border-top:1px solid rgba(255,255,255,.07);}
.sb-user{
  display:flex;align-items:center;gap:10px;
  padding:10px 12px;border-radius:9px;
  background:rgba(255,255,255,.05);margin-bottom:8px;
}
.sb-avatar{
  width:32px;height:32px;border-radius:50%;flex-shrink:0;
  background:linear-gradient(135deg,${T.gold},${T.goldLt});
  display:flex;align-items:center;justify-content:center;
  font-weight:800;font-size:12px;color:${T.forestDk};
}
.sb-uname{font-size:13px;font-weight:600;color:${T.white};line-height:1.2;}
.sb-urole{font-size:10px;color:rgba(255,255,255,.38);margin-top:1px;}
.logout-btn{
  width:100%;padding:9px 12px;
  background:rgba(139,58,26,.2);border:1px solid rgba(139,58,26,.3);
  color:#f87171;border-radius:9px;cursor:pointer;
  font-family:'DM Sans',sans-serif;font-size:13px;font-weight:600;
  transition:all .17s;display:flex;align-items:center;justify-content:center;gap:6px;
}
.logout-btn:hover:not(:disabled){background:rgba(139,58,26,.35);}
.logout-btn:disabled{opacity:.5;cursor:not-allowed;}

/* ── MOBILE TOPBAR ── */
.mobile-topbar{
  display:none;
  background:${T.forestDk};
  padding:12px 16px;
  align-items:center;
  justify-content:space-between;
  position:sticky;top:0;z-index:150;
  border-bottom:1px solid rgba(255,255,255,.08);
}
.hamburger{
  background:none;border:none;cursor:pointer;
  display:flex;flex-direction:column;gap:5px;padding:4px;
}
.hamburger span{display:block;width:22px;height:2px;background:${T.white};border-radius:2px;transition:all .2s;}
.mobile-logo{font-family:'Playfair Display',serif;font-size:16px;font-weight:900;color:${T.white};}
.mobile-role{font-size:11px;color:${T.goldLt};font-weight:700;}

/* ── SIDEBAR OVERLAY (mobile) ── */
.sidebar-overlay{
  display:none;position:fixed;inset:0;background:rgba(0,0,0,.5);z-index:190;
  backdrop-filter:blur(2px);
}

/* ── CONTENT ── */
.content{flex:1;overflow-x:hidden;padding:clamp(18px,3vw,34px) clamp(14px,3vw,38px);}

/* ── TOPBAR ── */
.topbar{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:26px;flex-wrap:wrap;gap:12px;}
.topbar-title{font-family:'Playfair Display',serif;font-size:clamp(20px,3vw,28px);font-weight:900;color:${T.ink};}
.topbar-sub{font-size:12px;color:${T.smoke};margin-top:2px;}
.role-pill{font-size:11px;font-weight:700;padding:5px 12px;border-radius:50px;text-transform:uppercase;letter-spacing:.07em;}

/* ── KPI GRID ── */
.kpi-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(160px,1fr));gap:14px;margin-bottom:26px;}
.kpi-card{
  background:${T.white};border-radius:16px;border:1.5px solid ${T.creamDk};
  padding:18px 16px;position:relative;overflow:hidden;
  transition:transform .2s,box-shadow .2s;animation:fadeUp .5s ease both;
}
.kpi-card:hover{transform:translateY(-3px);box-shadow:0 10px 32px rgba(14,31,23,.1);}
.kpi-card::after{content:attr(data-icon);position:absolute;right:10px;bottom:6px;font-size:38px;opacity:.07;}
.kpi-label{font-size:10px;font-weight:600;color:${T.smoke};text-transform:uppercase;letter-spacing:.09em;margin-bottom:9px;}
.kpi-val{font-family:'Playfair Display',serif;font-size:clamp(22px,3vw,30px);font-weight:900;line-height:1;}

/* ── REVENUE HERO ── */
.rev-hero{
  background:linear-gradient(135deg,${T.forestDk} 0%,${T.forest} 65%,${T.forestMid} 100%);
  border-radius:18px;padding:clamp(20px,3vw,30px);
  display:flex;align-items:center;justify-content:space-between;
  gap:14px;flex-wrap:wrap;margin-bottom:26px;
  box-shadow:0 14px 44px rgba(14,31,23,.28);
  position:relative;overflow:hidden;animation:fadeUp .4s ease both;
}
.rev-hero::before{content:'';position:absolute;right:-50px;top:-50px;width:200px;height:200px;border-radius:50%;background:rgba(200,148,42,.1);}
.rev-label{font-size:11px;color:rgba(255,255,255,.45);text-transform:uppercase;letter-spacing:.12em;margin-bottom:9px;}
.rev-amount{font-family:'Playfair Display',serif;font-size:clamp(28px,5vw,46px);font-weight:900;color:${T.goldLt};line-height:1;}
.rev-sub{font-size:12px;color:rgba(255,255,255,.38);margin-top:7px;}
.rev-badges{display:flex;gap:8px;margin-top:14px;flex-wrap:wrap;}
.rev-badge{font-size:12px;font-weight:600;padding:4px 11px;border-radius:50px;background:rgba(255,255,255,.1);color:rgba(255,255,255,.75);}

/* ── SECTION ── */
.section{margin-bottom:30px;animation:fadeUp .5s ease both;}
.sec-head{display:flex;align-items:center;justify-content:space-between;margin-bottom:14px;flex-wrap:wrap;gap:10px;}
.sec-title{font-family:'Playfair Display',serif;font-size:18px;font-weight:700;}
.sec-badge{font-size:12px;background:rgba(26,61,43,.1);color:${T.forest};padding:4px 11px;border-radius:50px;font-weight:600;}
.sec-actions{display:flex;gap:8px;align-items:center;}

/* ── TABLE ── */
.tbl-wrap{background:${T.white};border-radius:13px;border:1.5px solid ${T.creamDk};overflow:hidden;}
.data-table{width:100%;border-collapse:collapse;}
.data-table th{background:${T.forest};color:rgba(255,255,255,.78);font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.09em;padding:11px 13px;text-align:left;}
.data-table td{padding:10px 13px;font-size:13px;border-bottom:1px solid ${T.creamDk};vertical-align:middle;}
.data-table tr:last-child td{border-bottom:none;}
.data-table tr:hover td{background:rgba(26,61,43,.025);}
.mono{font-family:'DM Mono',monospace;font-size:12px;}
.status-pill{font-size:11px;font-weight:700;padding:3px 9px;border-radius:50px;letter-spacing:.04em;white-space:nowrap;}
.badge{font-size:11px;font-weight:700;padding:3px 8px;border-radius:50px;display:inline-block;margin:1px;}
.badge-cls{background:rgba(26,61,43,.1);color:${T.forest};}

/* ── BTNS ── */
.btn{display:inline-flex;align-items:center;gap:6px;padding:8px 14px;border-radius:9px;border:none;cursor:pointer;font-family:'DM Sans',sans-serif;font-size:13px;font-weight:600;transition:all .17s;white-space:nowrap;}
.btn-primary{background:${T.forest};color:${T.white};box-shadow:0 4px 12px rgba(26,61,43,.22);}
.btn-primary:hover{background:${T.forestDk};transform:translateY(-1px);}
.btn-primary:disabled{opacity:.55;cursor:not-allowed;transform:none;}
.btn-gold{background:${T.gold};color:${T.forestDk};font-weight:700;}
.btn-gold:hover{background:${T.goldLt};}
.btn-ghost{background:rgba(26,61,43,.08);color:${T.forest};}
.btn-ghost:hover{background:rgba(26,61,43,.15);}
.btn-danger{background:rgba(139,58,26,.1);color:${T.rust};}
.btn-danger:hover{background:rgba(139,58,26,.2);}
.btn-sm{padding:5px 10px;font-size:12px;border-radius:7px;}
.btn-full{width:100%;justify-content:center;padding:12px;font-size:14px;}
.btn-print{background:rgba(27,63,110,.1);color:${T.blue};border:1px solid rgba(27,63,110,.2);}
.btn-print:hover{background:rgba(27,63,110,.18);}

/* ── FILTERS ── */
.filter-row{display:flex;gap:7px;flex-wrap:wrap;margin-bottom:13px;}
.filter-btn{padding:6px 13px;border-radius:20px;border:1.5px solid ${T.creamDk};background:${T.white};color:${T.smoke};cursor:pointer;font-size:12px;font-weight:600;transition:all .15s;font-family:'DM Sans',sans-serif;}
.filter-btn:hover{border-color:${T.forest};color:${T.forest};}
.filter-btn.active{background:${T.forest};color:${T.white};border-color:${T.forest};}

/* ── SEARCH BAR ── */
.search-wrap{background:${T.white};border-radius:13px;border:1.5px solid ${T.creamDk};padding:16px;margin-bottom:18px;}
.search-grid{display:grid;grid-template-columns:1fr 1fr 1fr auto;gap:11px;align-items:end;}
@media(max-width:680px){.search-grid{grid-template-columns:1fr 1fr;}}
@media(max-width:420px){.search-grid{grid-template-columns:1fr;}}
.field{display:flex;flex-direction:column;gap:5px;}
.field label{font-size:10px;font-weight:700;color:${T.smoke};text-transform:uppercase;letter-spacing:.1em;}
.field input,.field select{width:100%;padding:10px 12px;border:1.5px solid ${T.creamDk};border-radius:9px;font-family:'DM Sans',sans-serif;font-size:14px;font-weight:500;color:${T.ink};background:#f5f0e5;outline:none;transition:border-color .17s,background .17s;appearance:none;}
.field input:focus,.field select:focus{border-color:${T.forest};background:${T.white};box-shadow:0 0 0 3px rgba(26,61,43,.07);}

/* ── AUTOCOMPLETE ── */
.ac-wrap{position:relative;}
.ac-list{position:absolute;top:calc(100% + 4px);left:0;right:0;background:${T.white};border:1.5px solid ${T.creamDk};border-radius:10px;z-index:300;box-shadow:0 10px 32px rgba(14,31,23,.13);max-height:190px;overflow-y:auto;animation:fadeUp .15s ease both;}
.ac-item{padding:9px 13px;font-size:13px;cursor:pointer;transition:background .12s;display:flex;align-items:center;gap:8px;}
.ac-item:hover,.ac-item.active{background:rgba(26,61,43,.07);color:${T.forest};}

/* ── TRIP CARDS ── */
.trips-list{display:flex;flex-direction:column;gap:11px;}
.trip-card{background:${T.white};border-radius:13px;border:1.5px solid ${T.creamDk};padding:15px 18px;display:grid;grid-template-columns:1fr auto 1fr auto auto;gap:13px;align-items:center;transition:all .22s;cursor:pointer;animation:fadeUp .4s ease both;}
.trip-card:hover{border-color:${T.forest};transform:translateY(-2px);box-shadow:0 10px 30px rgba(14,31,23,.1);}
@media(max-width:700px){.trip-card{grid-template-columns:1fr 1fr;}}
.st-time{font-family:'DM Mono',monospace;font-size:20px;font-weight:500;line-height:1;}
.st-name{font-size:13px;font-weight:600;color:${T.forest};margin-top:2px;}
.trip-mid{display:flex;flex-direction:column;align-items:center;gap:5px;}
.trip-line{width:68px;display:flex;align-items:center;gap:3px;}
.trip-dot{width:6px;height:6px;border-radius:50%;background:${T.forest};flex-shrink:0;}
.trip-dash{flex:1;height:2px;background:${T.creamDk};position:relative;}
.trip-dash::after{content:'';border-top:2px dotted currentColor;position:absolute;top:50%;left:0;right:0;}
.trip-dur{font-size:11px;color:${T.smoke};background:${T.creamDk};padding:2px 8px;border-radius:50px;font-weight:500;}
.trip-price-val{font-family:'Playfair Display',serif;font-size:20px;font-weight:700;color:${T.forest};}
.trip-price-cur{font-size:11px;color:${T.smoke};}

/* ── DRAWER ── */
.overlay{position:fixed;inset:0;background:rgba(10,24,16,.5);z-index:500;backdrop-filter:blur(2px);}
.drawer{position:fixed;top:0;right:0;bottom:0;width:100%;max-width:480px;background:${T.white};z-index:501;overflow-y:auto;display:flex;flex-direction:column;border-left:1.5px solid ${T.creamDk};box-shadow:-14px 0 48px rgba(14,31,23,.18);}
.dhead{background:${T.forestDk};color:${T.white};padding:17px 20px;display:flex;align-items:center;gap:12px;flex-shrink:0;position:sticky;top:0;z-index:10;}
.dclose{background:rgba(255,255,255,.12);border:none;color:${T.white};width:30px;height:30px;border-radius:7px;cursor:pointer;font-size:16px;display:flex;align-items:center;justify-content:center;flex-shrink:0;transition:background .15s;}
.dclose:hover{background:rgba(255,255,255,.22);}
.dtitle{font-family:'Playfair Display',serif;font-size:17px;font-weight:700;flex:1;}
.dbody{padding:18px;display:flex;flex-direction:column;gap:15px;flex:1;}

.tsb{background:#f0ebe0;border-radius:11px;padding:13px 15px;display:flex;justify-content:space-between;align-items:flex-start;gap:12px;}
.tsb-route{font-weight:700;font-size:15px;color:${T.forest};}
.tsb-meta{font-size:12px;color:${T.smoke};margin-top:3px;}
.tsb-price{font-family:'Playfair Display',serif;font-size:21px;font-weight:700;color:${T.forest};white-space:nowrap;}

.dlabel{font-size:10px;font-weight:700;color:${T.smoke};text-transform:uppercase;letter-spacing:.09em;margin-bottom:6px;display:block;}
.dinput{width:100%;padding:10px 13px;border:1.5px solid ${T.creamDk};border-radius:9px;font-family:'DM Sans',sans-serif;font-size:14px;background:#f5f0e5;outline:none;transition:border-color .17s;}
.dinput:focus{border-color:${T.forest};background:${T.white};}
.dselect{width:100%;padding:10px 13px;border:1.5px solid ${T.creamDk};border-radius:9px;font-family:'DM Sans',sans-serif;font-size:14px;background:#f5f0e5;color:${T.ink};outline:none;appearance:none;cursor:pointer;}
.dselect:focus{border-color:${T.forest};background:${T.white};}

.fg2{display:grid;grid-template-columns:1fr 1fr;gap:13px;}
@media(max-width:480px){.fg2{grid-template-columns:1fr;}}

/* ── SEAT GRID ── */
.seat-grid{display:grid;grid-template-columns:repeat(8,1fr);gap:6px;margin-top:8px;}
.seat-btn{
  aspect-ratio:1;border-radius:7px;border:1.5px solid ${T.creamDk};
  background:${T.white};cursor:pointer;font-size:11px;font-weight:700;
  display:flex;align-items:center;justify-content:center;
  transition:all .15s;font-family:'DM Mono',monospace;
}
.seat-btn:hover{border-color:${T.forest};background:rgba(26,61,43,.08);}
.seat-btn.selected{background:${T.forest};color:${T.white};border-color:${T.forest};}
.seat-btn.occupied{background:#fee2e2;color:#991b1b;border-color:#fca5a5;cursor:not-allowed;}
.seat-occupied-alert{
  background:#fee2e2;border:2px solid #f87171;border-radius:10px;
  padding:14px 16px;text-align:center;margin-top:8px;
}
.seat-occupied-alert-text{font-size:17px;font-weight:900;color:#991b1b;line-height:1.3;}
.seat-legend{display:flex;gap:12px;margin-top:10px;flex-wrap:wrap;}
.seat-legend-item{display:flex;align-items:center;gap:5px;font-size:11px;color:${T.smoke};}
.seat-legend-dot{width:12px;height:12px;border-radius:3px;border:1.5px solid;}

/* ── PAY OPTS ── */
.pay-opts{display:flex;flex-direction:column;gap:7px;}
.pay-opt{display:flex;align-items:center;gap:11px;padding:11px 13px;border-radius:10px;border:1.5px solid ${T.creamDk};background:#f5f0e5;cursor:pointer;transition:all .15s;}
.pay-opt:hover{border-color:${T.forestMid};}
.pay-opt.sel{border-color:${T.forest};background:rgba(26,61,43,.06);}
.pay-opt-icon{font-size:19px;flex-shrink:0;}
.pay-opt-name{font-weight:600;font-size:13px;color:${T.ink};}

.pay-info{background:#f0ebe0;border-radius:10px;padding:12px 14px;border-left:3px solid ${T.gold};}
.pay-info-title{font-weight:700;font-size:13px;color:${T.forest};margin-bottom:8px;}
.pay-steps-list{list-style:none;display:flex;flex-direction:column;gap:7px;}
.pay-step{display:flex;align-items:flex-start;gap:8px;font-size:13px;}
.pay-num{width:19px;height:19px;border-radius:50%;background:${T.forest};color:${T.white};font-size:10px;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
.pay-tip{margin-top:8px;font-size:12px;color:${T.smoke};border-top:1px solid ${T.creamDk};padding-top:7px;}

/* ── CASH RECEIPT BOX ── */
.cash-receipt{
  background:${T.forestDk};border-radius:12px;padding:18px;
  border:2px dashed ${T.gold};text-align:center;
}
.cash-receipt-label{font-size:11px;color:rgba(255,255,255,.45);text-transform:uppercase;letter-spacing:.12em;margin-bottom:6px;}
.cash-receipt-amount{font-family:'Playfair Display',serif;font-size:36px;font-weight:900;color:${T.goldLt};line-height:1;}
.cash-receipt-sub{font-size:12px;color:rgba(255,255,255,.5);margin-top:5px;}

/* ── REF BOX ── */
.ref-box{background:${T.creamDk};border-radius:8px;padding:10px 13px;font-family:'DM Mono',monospace;font-size:13px;color:${T.forest};font-weight:600;}
.ref-input-wrap{position:relative;}
.ref-input-icon{position:absolute;left:12px;top:50%;transform:translateY(-50%);font-size:16px;}
.ref-input{padding-left:36px !important;}

/* ── DONE ── */
.done-box{text-align:center;display:flex;flex-direction:column;align-items:center;gap:11px;padding:6px 0;}
.done-icon{font-size:50px;}
.done-title{font-family:'Playfair Display',serif;font-size:22px;font-weight:700;color:${T.forest};}
.done-ref{font-size:13px;color:${T.smoke};}

.tp{background:${T.forestDk};border-radius:11px;padding:15px;border-left:5px solid ${T.gold};display:flex;flex-direction:column;gap:9px;width:100%;}
.tp-head{display:flex;justify-content:space-between;align-items:center;font-size:12px;}
.tp-route{font-family:'Playfair Display',serif;font-size:17px;font-weight:700;color:${T.white};}
.tp-arrow{color:${T.gold};margin:0 6px;}
.tp-details{display:flex;flex-wrap:wrap;gap:5px;border-top:1px dashed rgba(255,255,255,.14);padding-top:8px;}
.tp-details span{font-size:11px;color:rgba(255,255,255,.6);background:rgba(255,255,255,.08);padding:2px 7px;border-radius:50px;}
.tp-footer{display:flex;justify-content:space-between;font-size:12px;color:rgba(255,255,255,.68);border-top:1px dashed rgba(255,255,255,.14);padding-top:8px;}

/* ── FILL BAR ── */
.fill-bar{margin-top:4px;height:4px;background:${T.creamDk};border-radius:2px;width:56px;}
.fill-inner{height:100%;border-radius:2px;transition:width .3s;}

/* ── BAR CHART ── */
.bar-chart{display:flex;align-items:flex-end;gap:5px;height:110px;padding:0 2px;}
.bar-wrap{display:flex;flex-direction:column;align-items:center;gap:3px;flex:1;}
.bar{border-radius:4px 4px 0 0;width:100%;min-width:14px;}
.bar-lbl{font-size:9px;color:${T.smoke};text-align:center;}
.bar-v{font-size:9px;font-weight:700;color:${T.forest};}

/* ── EMPTY ── */
.empty{text-align:center;padding:48px 20px;background:${T.white};border-radius:13px;border:1.5px dashed ${T.creamDk};}
.empty-icon{font-size:42px;margin-bottom:10px;}
.empty-text{color:${T.smoke};font-size:14px;line-height:1.6;}

/* ── PAGER ── */
.pager{display:flex;justify-content:center;gap:6px;margin-top:16px;}
.pager-btn{padding:6px 12px;border-radius:8px;border:1.5px solid ${T.creamDk};background:${T.white};color:${T.ink};cursor:pointer;font-size:13px;font-weight:600;transition:all .14s;font-family:'DM Sans',sans-serif;}
.pager-btn:hover{border-color:${T.forest};color:${T.forest};}
.pager-btn.active{background:${T.forest};color:${T.white};border-color:${T.forest};}
.pager-btn:disabled{opacity:.4;cursor:not-allowed;}

/* ── TOAST ── */
.toast{position:fixed;top:18px;right:18px;z-index:9000;padding:11px 17px;border-radius:11px;font-size:14px;font-weight:600;box-shadow:0 8px 24px rgba(0,0,0,.14);max-width:300px;animation:fadeUp .22s ease both;}
.toast-success{background:#d1fae5;color:#065f46;}
.toast-error{background:#fee2e2;color:#991b1b;}
.toast-warning{background:#fef3c7;color:#92400e;}

/* ── LOADING ── */
.loading-full{display:flex;align-items:center;justify-content:center;height:100vh;font-size:15px;color:${T.smoke};}
.spinner{width:30px;height:30px;border:3px solid ${T.creamDk};border-top-color:${T.forest};border-radius:50%;animation:spin .8s linear infinite;margin-right:11px;}

/* ── AGENT HERO ── */
.agent-hero{
  background:linear-gradient(135deg,${T.blue} 0%,#2d5a9e 100%);
  border-radius:18px;padding:clamp(20px,3vw,28px);
  display:flex;align-items:center;justify-content:space-between;
  gap:14px;flex-wrap:wrap;margin-bottom:22px;
  box-shadow:0 14px 44px rgba(27,63,110,.28);
  position:relative;overflow:hidden;animation:fadeUp .4s ease both;
}
.agent-hero::before{content:'';position:absolute;right:-50px;top:-50px;width:200px;height:200px;border-radius:50%;background:rgba(255,255,255,.06);}

/* ── PROG HERO ── */
.prog-hero{
  background:linear-gradient(135deg,${T.forestDk} 0%,${T.forest} 100%);
  border-radius:18px;padding:clamp(20px,3vw,28px);
  display:flex;align-items:center;justify-content:space-between;
  gap:14px;flex-wrap:wrap;margin-bottom:22px;
  box-shadow:0 14px 44px rgba(14,31,23,.28);
  position:relative;overflow:hidden;animation:fadeUp .4s ease both;
}

/* ── PRINT TICKET ── */
.print-ticket-modal{
  position:fixed;inset:0;background:rgba(0,0,0,.6);z-index:800;
  display:flex;align-items:center;justify-content:center;padding:20px;
  backdrop-filter:blur(3px);
}
.print-ticket-inner{
  background:${T.white};border-radius:16px;padding:0;
  max-width:560px;width:100%;box-shadow:0 20px 60px rgba(0,0,0,.3);
  overflow:hidden;
}
.print-ticket-actions{
  display:flex;gap:10px;padding:16px 20px;background:#f0ebe0;
  border-top:1px solid ${T.creamDk};justify-content:flex-end;
}

/* ── TICKET PRINTABLE ── */
.ticket-printable{
  background:${T.white};
  font-family:'DM Sans',sans-serif;
}
.ticket-header{
  background:${T.forestDk};padding:18px 24px;
  display:flex;align-items:center;justify-content:space-between;
}
.ticket-brand{font-family:'Playfair Display',serif;font-size:20px;font-weight:900;color:${T.goldLt};}
.ticket-ref{font-family:'DM Mono',monospace;font-size:12px;color:rgba(255,255,255,.55);}
.ticket-body{padding:20px 24px;}
.ticket-route{font-family:'Playfair Display',serif;font-size:24px;font-weight:700;color:${T.forest};margin-bottom:4px;}
.ticket-route-arrow{color:${T.gold};margin:0 8px;}
.ticket-divider{border:none;border-top:2px dashed ${T.creamDk};margin:14px 0;}
.ticket-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin-bottom:14px;}
.ticket-field label{font-size:9px;font-weight:700;color:${T.smoke};text-transform:uppercase;letter-spacing:.1em;display:block;margin-bottom:3px;}
.ticket-field span{font-size:14px;font-weight:600;color:${T.ink};}
.ticket-price-row{display:flex;justify-content:space-between;align-items:center;background:#f0ebe0;padding:12px 16px;border-radius:9px;margin-top:4px;}
.ticket-price-label{font-size:12px;color:${T.smoke};font-weight:600;}
.ticket-price-val{font-family:'Playfair Display',serif;font-size:22px;font-weight:900;color:${T.forest};}
.ticket-footer{background:${T.forest};padding:10px 24px;font-size:11px;color:rgba(255,255,255,.55);text-align:center;}
.ticket-barcode{font-family:'DM Mono',monospace;font-size:18px;letter-spacing:.4em;color:rgba(255,255,255,.3);margin-top:4px;}

@media print {
  body * { visibility: hidden !important; }
  .ticket-printable, .ticket-printable * { visibility: visible !important; }
  .ticket-printable {
    position: fixed !important;
    left: 0; top: 0; width: 100%;
    box-shadow: none !important;
  }
  .print-ticket-actions { display: none !important; }
}

/* ── RESPONSIVE ── */
@media(max-width:768px){
  .sidebar{
    position:fixed;top:0;left:0;bottom:0;
    transform:translateX(-100%);
    z-index:200;
  }
  .sidebar.open{transform:translateX(0);}
  .sidebar-overlay{display:block;}
  .mobile-topbar{display:flex;}
  .content{padding:14px;}
  .layout{flex-direction:column;}
}

@keyframes fadeUp{from{opacity:0;transform:translateY(13px);}to{opacity:1;transform:translateY(0);}}
@keyframes spin{to{transform:rotate(360deg);}}
`;

/* ── AUTOCOMPLETE ── */
export function AcInput({ value, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const ref = useRef(null);
  const sugg = value.length >= 1
    ? STATIONS.filter(s => s.toLowerCase().startsWith(value.toLowerCase()) && s.toLowerCase() !== value.toLowerCase())
    : [];
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const pick = s => { onChange(s); setOpen(false); setActive(-1); };
  const onKey = e => {
    if (!open || !sugg.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive(a => Math.min(a+1,sugg.length-1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive(a => Math.max(a-1,-1)); }
    else if (e.key === "Enter") { e.preventDefault(); if (active>=0) pick(sugg[active]); else setOpen(false); }
    else if (e.key === "Escape") setOpen(false);
  };
  return (
    <div className="ac-wrap" ref={ref}>
      <input placeholder={placeholder} value={value} autoComplete="off"
        onChange={e => { onChange(e.target.value); setOpen(true); setActive(-1); }}
        onFocus={() => setOpen(true)} onKeyDown={onKey}
      />
      {open && sugg.length > 0 && (
        <div className="ac-list">
          {sugg.map((s,i) => (
            <div key={s} className={`ac-item${i===active?" active":""}`} onMouseDown={() => pick(s)}>{s}</div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── TOAST ── */
export function Toast({ toast }) {
  if (!toast) return null;
  return <div className={`toast toast-${toast.type}`}>{toast.msg}</div>;
}

/* ── TICKET IMPRIMABLE ── */
export function PrintTicketModal({ booking, onClose }) {
  const cls = CLASS_MAP[booking.travelClass] || CLASS_MAP.ECONOMIQUE;
  const st  = STATUS[booking.status] || STATUS.PENDING;
  const op  = booking.operator;

  const handlePrint = () => window.print();

  return (
    <div className="print-ticket-modal" onClick={onClose}>
      <div className="print-ticket-inner" onClick={e => e.stopPropagation()}>
        <div className="ticket-printable" id="ticket-to-print">
          {/* En-tête */}
          <div className="ticket-header">
            <div>
              <div className="ticket-brand" style={{display:"flex",alignItems:"center",gap:6}}><TrainIcon size={16}/> SETRAG</div>
              <div style={{fontSize:10,color:"rgba(255,255,255,.4)",marginTop:2}}>Société d'Exploitation du Transgabonais</div>
            </div>
            <div style={{textAlign:"right"}}>
              <div className="ticket-ref">{booking.reference}</div>
              <div style={{marginTop:6}}>
                <span style={{fontSize:11,fontWeight:700,padding:"3px 8px",borderRadius:20,
                  background:st.bg,color:st.color}}>{st.label}</span>
              </div>
            </div>
          </div>

          {/* Corps */}
          <div className="ticket-body">
            {/* Route */}
            <div className="ticket-route">
              {booking.route?.split("→")[0]?.trim() || "-"}
              <span className="ticket-route-arrow">→</span>
              {booking.route?.split("→")[1]?.trim() || "-"}
            </div>
            <div style={{fontSize:13,color:T.smoke,marginTop:3}}>Train {booking.trainNumber || "-"}</div>

            <hr className="ticket-divider"/>

            {/* Grille infos */}
            <div className="ticket-grid">
              <div className="ticket-field">
                <label>Passager</label>
                <span>{booking.fullName || "-"}</span>
              </div>
              <div className="ticket-field">
                <label>Classe</label>
                <span>{cls.icon} {cls.label}</span>
              </div>
              <div className="ticket-field">
                <label>Siège</label>
                <span>{booking.seatNumber ? `Nº ${booking.seatNumber}` : "Libre"}</span>
              </div>
              <div className="ticket-field">
                <label>Date voyage</label>
                <span>{booking.date?.split(" ")[0] || "-"}</span>
              </div>
              <div className="ticket-field">
                <label>Départ</label>
                <span>{booking.departureTime || "-"}</span>
              </div>
              <div className="ticket-field">
                <label>Arrivée</label>
                <span>{booking.arrivalTime || "-"}</span>
              </div>
            </div>

            {/* Paiement */}
            <div className="ticket-price-row">
              <div>
                <div className="ticket-price-label">Montant payé</div>
                <div style={{fontSize:11,color:T.smoke,marginTop:2}}>
                  {op === "CASH" ? "Espèces" : op === "AIRTEL_MONEY" ? "Airtel Money" : op === "MOOV_MONEY" ? "Moov Money" : op || "-"}
                </div>
              </div>
              <div className="ticket-price-val">{fmtPrice(booking.amount)}</div>
            </div>

            {/* Date d'émission */}
            <div style={{fontSize:11,color:T.smoke,marginTop:12,textAlign:"right"}}>
              Émis le {new Date().toLocaleDateString("fr-FR",{day:"2-digit",month:"long",year:"numeric"})} à {new Date().toLocaleTimeString("fr-FR",{hour:"2-digit",minute:"2-digit"})}
            </div>
          </div>

          {/* Pied */}
          <div className="ticket-footer">
            <div>Ce billet fait foi. Présentez-le à l'agent SETRAG à l'embarquement.</div>
            <div className="ticket-barcode">
              {(booking.reference || "").replace(/-/g,"").slice(0,12).padEnd(12,"0")}
            </div>
          </div>

          {booking.qrPayload && (
            <div style={{display:"flex",justifyContent:"center",padding:"12px 0 4px"}}>
              <AgentTicketQR data={booking.qrPayload} size={100} />
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="print-ticket-actions">
          <button className="btn btn-ghost btn-sm" onClick={onClose} style={{display:"inline-flex",alignItems:"center",gap:6}}><XIcon size={14}/> Fermer</button>
          <button className="btn btn-print btn-sm" onClick={handlePrint} style={{display:"inline-flex",alignItems:"center",gap:6}}><PrinterIcon size={14}/> Imprimer</button>
          <button className="btn btn-primary btn-sm" onClick={() => downloadTicketsPdfAgent([{
            reference: booking.reference, passenger: booking.fullName, seat: booking.seatNumber,
            depart: (booking.route||"").split("→")[0]?.trim(), arrivee: (booking.route||"").split("→")[1]?.trim(),
            date: booking.date?.split(" ")[0], heure: booking.departureTime, train: booking.trainNumber,
            classe: booking.travelClass, prix: booking.amount, tripType: "ALLER", qrPayload: booking.qrPayload || null,
          }], `billet-setrag-${booking.reference}.pdf`)}>⬇️ Télécharger PDF</button>
        </div>
      </div>
    </div>
  );
}

/* ── TICKET PNG DOWNLOAD (depuis liste réservations) ── */
export function downloadTicketPNGFromBooking(booking) {
  const W=800, H=380;
  const cls = CLASS_MAP[booking.travelClass] || CLASS_MAP.ECONOMIQUE;
  const cv = document.createElement("canvas"); cv.width=W; cv.height=H;
  const c = cv.getContext("2d");
  c.fillStyle=T.forest; c.fillRect(0,0,W,H);
  c.fillStyle=T.gold; c.fillRect(0,0,8,H);

  // White card
  c.fillStyle=T.white;
  c.beginPath();c.roundRect?c.roundRect(24,24,W-48,H-48,14):c.rect(24,24,W-48,H-48);c.fill();

  // Header bg
  c.fillStyle=T.forest;
  c.beginPath();c.roundRect?c.roundRect(24,24,W-48,72,{upperLeft:14,upperRight:14,lowerLeft:0,lowerRight:0}):c.rect(24,24,W-48,72);c.fill();

  c.fillStyle=T.gold; c.font="bold 20px serif"; c.fillText("SETRAG",48,66);
  c.fillStyle="rgba(255,255,255,0.5)"; c.font="12px sans-serif"; c.fillText("Billet électronique",220,66);
  c.fillStyle=T.gold; c.font="bold 12px monospace"; c.textAlign="right"; c.fillText(booking.reference||"",W-48,66); c.textAlign="left";

  const parts = (booking.route||"-→-").split("→");
  const dep = parts[0]?.trim()||"-"; const arr = parts[1]?.trim()||"-";
  c.fillStyle=T.forest; c.font="bold 26px serif"; c.fillText(dep,48,128);
  c.fillStyle=T.smoke; c.font="20px sans-serif"; c.fillText("→",48+c.measureText(dep).width+10,128);
  c.fillStyle=T.forest; c.font="bold 26px serif"; c.fillText(arr,48+c.measureText(dep).width+42,128);

  c.setLineDash([6,4]); c.strokeStyle="#ddd"; c.lineWidth=1;
  c.beginPath(); c.moveTo(48,150); c.lineTo(W-48,150); c.stroke(); c.setLineDash([]);

  const cols=[
    {l:"Passager",v:booking.fullName||"-"},
    {l:"Classe",v:cls.label},
    {l:"Siège",v:booking.seatNumber?"Nº "+booking.seatNumber:"Libre"},
    {l:"Date",v:booking.date?.split(" ")[0]||"-"},
    {l:"Départ",v:booking.departureTime||"-"},
    {l:"Train",v:booking.trainNumber||"-"},
  ];
  const colW=(W-96)/cols.length;
  cols.forEach(({l,v},i)=>{
    const x=48+i*colW;
    c.fillStyle=T.smoke; c.font="10px sans-serif"; c.fillText(l.toUpperCase(),x,177);
    c.fillStyle=T.ink; c.font="bold 13px sans-serif"; c.fillText(String(v).slice(0,14),x,195);
  });

  c.fillStyle=T.forest; c.font="bold 24px serif"; c.textAlign="right"; c.fillText(fmtPrice(booking.amount),W-48,195); c.textAlign="left";
  c.fillStyle=T.smoke; c.font="11px sans-serif";
  c.fillText(`Statut : ${STATUS[booking.status]?.label||"-"}`,48,240);
  c.textAlign="right"; c.fillText(`Émis le ${new Date().toLocaleDateString("fr-FR")}`,W-48,240); c.textAlign="left";
  c.fillStyle="#b07a1a"; c.font="10px sans-serif";
  c.fillText("Ce billet fait foi. Présentez-le à l'agent SETRAG à l'embarquement.",48,290);

  const lnk=document.createElement("a");
  lnk.download=`billet-${booking.reference||"SETRAG"}.png`;
  lnk.href=cv.toDataURL("image/png"); lnk.click();
}

/* ── TICKET PNG DOWNLOAD (depuis drawer de réservation) ── */
export function downloadTicketPNG({ trip, passengerName, bookingData, seat, operator }) {
  const W=800,H=380;
  const cv=document.createElement("canvas");cv.width=W;cv.height=H;
  const c=cv.getContext("2d");
  c.fillStyle=T.forest;c.fillRect(0,0,W,H);
  c.fillStyle=T.gold;c.fillRect(0,0,8,H);
  c.fillStyle=T.white;
  c.beginPath();c.rect(24,24,W-48,H-48);c.fill();
  c.fillStyle=T.forest;
  c.beginPath();c.rect(24,24,W-48,72);c.fill();
  c.fillStyle=T.gold;c.font="bold 20px serif";c.fillText("SETRAG",48,66);
  c.fillStyle="rgba(255,255,255,0.5)";c.font="12px sans-serif";c.fillText("Billet électronique",200,66);
  c.fillStyle=T.gold;c.font="bold 12px monospace";c.textAlign="right";c.fillText(bookingData.reference,W-48,66);c.textAlign="left";
  const dep=trip.departure?.name||trip.departure;const arr=trip.arrival?.name||trip.arrival;
  c.fillStyle=T.forest;c.font="bold 28px serif";c.fillText(dep,48,132);
  c.fillStyle=T.smoke;c.font="20px sans-serif";c.fillText("→",48+c.measureText(dep).width+10,132);
  c.fillStyle=T.forest;c.font="bold 28px serif";c.fillText(arr,48+c.measureText(dep).width+42,132);
  c.setLineDash([6,4]);c.strokeStyle="#ddd";c.lineWidth=1;c.beginPath();c.moveTo(48,156);c.lineTo(W-48,156);c.stroke();c.setLineDash([]);
  const cls=CLASS_MAP[trip.travelClass]||CLASS_MAP.ECONOMIQUE;
  const cols=[{l:"Départ",v:trip.departureTime||"-"},{l:"Arrivée",v:trip.arrivalTime||"-"},{l:"Train",v:trip.trainNumber||"-"},{l:"Classe",v:cls.label},{l:"Siège",v:seat?`Nº ${seat}`:"Libre"},{l:"Passager",v:passengerName||"-"}];
  const colW=(W-96)/cols.length;
  cols.forEach(({l,v},i)=>{const x=48+i*colW;c.fillStyle=T.smoke;c.font="10px sans-serif";c.fillText(l.toUpperCase(),x,183);c.fillStyle=T.ink;c.font="bold 13px sans-serif";c.fillText(v,x,201);});
  c.fillStyle=T.forest;c.font="bold 26px serif";c.textAlign="right";c.fillText(fmtPrice(trip.price),W-48,201);c.textAlign="left";
  c.fillStyle=T.smoke;c.font="11px sans-serif";c.fillText(`Payé via ${operator==="CASH"?"Espèces":operator||"-"}`,48,248);
  c.textAlign="right";c.fillText(`Émis le ${new Date().toLocaleDateString("fr-FR",{day:"2-digit",month:"long",year:"numeric"})}`,W-48,248);c.textAlign="left";
  c.fillStyle="#b07a1a";c.font="10px sans-serif";c.fillText("Ce billet fait foi. Présentez-le à l'agent SETRAG à l'embarquement.",48,298);
  const lnk=document.createElement("a");lnk.download=`billet-${bookingData.reference}.png`;lnk.href=cv.toDataURL("image/png");lnk.click();
}

/* ══════════════════════════════════════════════
   BOOKING DRAWER - Agent only
══════════════════════════════════════════════ */
export function BookingDrawer({ trip, returnTrip, onClose, onConfirmed, showToast }) {
  const isRT = !!returnTrip;
  const [seat, setSeat]             = useState(null);
  const [returnSeat, setReturnSeat] = useState(null);
  const [occupiedAlert, setOccupiedAlert] = useState(false);
  const [operator, setOperator]   = useState(null);
  const [passenger, setPassenger] = useState("");
  const [bData, setBData]         = useState(null);       // billet aller
  const [returnBData, setReturnBData] = useState(null);   // billet retour
  const [payRef, setPayRef]       = useState("");
  const [loading, setLoading]     = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const busy = useRef(false);

  const occupiedSeats = trip.occupiedSeats || [];
  const returnOccupiedSeats = returnTrip?.occupiedSeats || [];
  const cls = CLASS_MAP[trip.travelClass] || CLASS_MAP.ECONOMIQUE;
  const returnCls = returnTrip ? (CLASS_MAP[returnTrip.travelClass] || CLASS_MAP.ECONOMIQUE) : null;
  const totalAmount = Number(trip.price || 0) + (isRT ? Number(returnTrip.price || 0) : 0);

  useEffect(()=>{document.body.style.overflow="hidden";return()=>{document.body.style.overflow="";};}, []);

  const handleSeatClick = (n, isReturn=false) => {
    const taken = isReturn ? returnOccupiedSeats : occupiedSeats;
    if (taken.includes(n)) {
      setOccupiedAlert(true);
      setTimeout(() => setOccupiedAlert(false), 3000);
      return;
    }
    setOccupiedAlert(false);
    if (isReturn) setReturnSeat(prev => prev === n ? null : n);
    else setSeat(prev => prev === n ? null : n);
  };

  // Étape 1 -> déclenche la modale de confirmation Bootstrap plutôt qu'un window.confirm()
  const askConfirm = () => {
    if (!operator) { showToast("Choisissez un mode de paiement","warning"); return; }
    if (!passenger.trim()) { showToast("Nom du passager requis","warning"); return; }
    setShowConfirm(true);
  };

  // Crée le(s) billet(s) - aller ET retour en une seule opération simultanée si aller-retour.
  const handleBook = async () => {
    setShowConfirm(false);
    if (busy.current) return; busy.current=true; setLoading(true);
    try {
      const calls = [
        api.post("/bookings", {
          tripId: trip.id, fullName: passenger, seatNumber: seat || null, operator,
          tripType: "ALLER", isRoundTrip: isRT, returnDate: isRT ? returnTrip.date : null,
        }),
      ];
      if (isRT) {
        calls.push(api.post("/bookings", {
          tripId: returnTrip.id, fullName: passenger, seatNumber: returnSeat || null, operator,
          tripType: "RETOUR", isRoundTrip: true, returnDate: returnTrip.date,
        }));
      }
      const [res, resReturn] = await Promise.all(calls);
      const p = res?.data?.data ?? res?.data ?? {};
      setBData({ bookingId: p.bookingId??p.id??null, reference: p.reference??p.ref??"REF-"+Date.now(), qrPayload: p.qrPayload || null });
      if (isRT) {
        const p2 = resReturn?.data?.data ?? resReturn?.data ?? {};
        setReturnBData({ bookingId: p2.bookingId??p2.id??null, reference: p2.reference??p2.ref??"REF-"+Date.now(), qrPayload: p2.qrPayload || null });
      }
    } catch(e) { showToast(e?.response?.data?.error??e?.message??"Erreur","error"); }
    finally { setLoading(false); busy.current=false; }
  };

  const handlePay = async () => {
    if (operator !== "CASH" && !payRef.trim()) {
      showToast("Entrez la référence de paiement reçue par SMS","warning"); return;
    }
    if (busy.current) return; busy.current=true; setLoading(true);
    try {
      const txRef = operator === "CASH" ? "CASH-"+Date.now() : payRef.trim();
      const calls = [
        api.post("/verify-payment", { bookingId: bData.bookingId, transactionReference: txRef, operator, phoneNumber: "" }),
      ];
      if (isRT) calls.push(api.post("/verify-payment", { bookingId: returnBData.bookingId, transactionReference: txRef, operator, phoneNumber: "" }));
      const [res, resReturn] = await Promise.all(calls);
      setBData(p=>({...p, paid:true, qrPayload: res?.data?.booking?.qrPayload || p.qrPayload }));
      if (isRT) setReturnBData(p=>({...p, paid:true, qrPayload: resReturn?.data?.booking?.qrPayload || p.qrPayload }));
      onConfirmed();
      showToast("Achat confirmé - Bon voyage !","success");
    } catch(e) { showToast(e?.response?.data?.error??e?.message??"Erreur paiement","error"); }
    finally { setLoading(false); busy.current=false; }
  };

  const handleDownload = async () => {
    const tickets = [{
      reference: bData.reference, passenger, seat,
      depart: trip.departure?.name||trip.departure, arrivee: trip.arrival?.name||trip.arrival,
      date: trip.date, heure: trip.departureTime, train: trip.trainNumber, classe: trip.travelClass,
      prix: trip.price, tripType: "ALLER", qrPayload: bData.qrPayload,
    }];
    if (isRT && returnBData) {
      tickets.push({
        reference: returnBData.reference, passenger, seat: returnSeat,
        depart: returnTrip.departure?.name||returnTrip.departure, arrivee: returnTrip.arrival?.name||returnTrip.arrival,
        date: returnTrip.date, heure: returnTrip.departureTime, train: returnTrip.trainNumber, classe: returnTrip.travelClass,
        prix: returnTrip.price, tripType: "RETOUR", qrPayload: returnBData.qrPayload,
      });
    }
    await downloadTicketsPdfAgent(tickets, `billets-setrag-${bData.reference}.pdf`);
    showToast("Billet(s) téléchargé(s)","success");
  };

  const step = bData?.paid ? 3 : bData ? 2 : 1;

  const SeatGrid = ({ tripRef, isReturn }) => {
    const taken = isReturn ? returnOccupiedSeats : occupiedSeats;
    const current = isReturn ? returnSeat : seat;
    return (
      <div className="seat-grid">
        {ALL_SEATS.slice(0, tripRef.totalSeats || 80).map(n => {
          const isOccupied = taken.includes(n);
          const isSelected = current === n;
          return (
            <button key={n} className={`seat-btn${isSelected?" selected":""}${isOccupied?" occupied":""}`}
              onClick={() => handleSeatClick(n, isReturn)} title={isOccupied?"Occupé":"Siège "+n}
            >{n}</button>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <div className="overlay" onClick={onClose}/>
      <div className="drawer">
        <div className="dhead">
          <button className="dclose" onClick={onClose}>←</button>
          <div className="dtitle">
            {step===3 ? "Billet(s) confirmé(s)" : step===2 ? "Confirmation paiement" : isRT ? "Émission billets aller-retour" : "Émission de billet"}
          </div>
          <div style={{display:"flex",gap:5,marginLeft:"auto"}}>
            {[1,2,3].map(s=>(
              <div key={s} style={{width:22,height:22,borderRadius:"50%",background:step>=s?"rgba(200,148,42,.9)":"rgba(255,255,255,.2)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:step>=s?T.forestDk:T.white}}>{s}</div>
            ))}
          </div>
        </div>

        <div className="dbody">
          <div className="tsb">
            <div>
              <div className="tsb-route">→ {trip.departure?.name||trip.departure} → {trip.arrival?.name||trip.arrival}</div>
              <div className="tsb-meta">{trip.departureTime} – {trip.arrivalTime} · {cls.icon} {cls.label} · {trip.trainNumber} · {trip.date}</div>
            </div>
            <div className="tsb-price">{fmtPrice(trip.price)}</div>
          </div>
          {isRT && (
            <div className="tsb" style={{borderColor:"rgba(200,148,42,.4)"}}>
              <div>
                <div className="tsb-route">↩ {returnTrip.departure?.name||returnTrip.departure} → {returnTrip.arrival?.name||returnTrip.arrival}</div>
                <div className="tsb-meta">{returnTrip.departureTime} – {returnTrip.arrivalTime} · {returnCls.icon} {returnCls.label} · {returnTrip.trainNumber} · {returnTrip.date}</div>
              </div>
              <div className="tsb-price">{fmtPrice(returnTrip.price)}</div>
            </div>
          )}

          {step===1 && <>
            <div>
              <label className="dlabel">Nom complet du passager *</label>
              <input className="dinput" placeholder="Ex : Jean-Pierre Mouele" value={passenger} onChange={e=>setPassenger(e.target.value)}/>
            </div>

            {occupiedAlert && (
              <div className="seat-occupied-alert">
                <div className="seat-occupied-alert-text" style={{display:"flex",alignItems:"center",gap:6}}><AlertIcon size={14}/> CE SIÈGE EST DÉJÀ OCCUPÉ</div>
                <div style={{fontSize:12,color:"#991b1b",marginTop:4}}>Veuillez sélectionner un autre siège disponible.</div>
              </div>
            )}

            <div>
              <label className="dlabel">Siège aller (optionnel)</label>
              <SeatGrid tripRef={trip} isReturn={false} />
              <div className="seat-legend">
                <div className="seat-legend-item"><div className="seat-legend-dot" style={{background:T.white,borderColor:T.creamDk}}/> Disponible</div>
                <div className="seat-legend-item"><div className="seat-legend-dot" style={{background:T.forest,borderColor:T.forest}}/> Sélectionné ({seat ? `Nº ${seat}` : "aucun"})</div>
                <div className="seat-legend-item"><div className="seat-legend-dot" style={{background:"#fee2e2",borderColor:"#fca5a5"}}/> Occupé</div>
              </div>
            </div>

            {isRT && (
              <div>
                <label className="dlabel">Siège retour (optionnel)</label>
                <SeatGrid tripRef={returnTrip} isReturn={true} />
                <div className="seat-legend">
                  <div className="seat-legend-item"><div className="seat-legend-dot" style={{background:T.forest,borderColor:T.forest}}/> Sélectionné ({returnSeat ? `Nº ${returnSeat}` : "aucun"})</div>
                </div>
              </div>
            )}

            <div>
              <label className="dlabel">Mode de paiement *</label>
              <div className="pay-opts">
                {OPERATORS_ALL.map(op=>(
                  <div key={op.id} className={`pay-opt${operator===op.id?" sel":""}`} onClick={()=>setOperator(op.id)}>
                    <span className="pay-opt-icon">{op.icon}</span>
                    <span className="pay-opt-name">{op.name}</span>
                    {operator===op.id&&<span style={{marginLeft:"auto",color:T.forest,fontWeight:700}}>✓</span>}
                  </div>
                ))}
              </div>
            </div>

            <div className="alert alert-light border d-flex justify-content-between align-items-center">
              <span>Total {isRT ? "(aller + retour)" : ""}</span>
              <strong style={{color:T.forest,fontSize:16}}>{fmtPrice(totalAmount)}</strong>
            </div>

            <button className="btn btn-primary btn-full" onClick={askConfirm} disabled={loading||!operator}>
              {loading?"⏳ Création…":"Continuer → Paiement"}
            </button>
          </>}

          {step===2 && <>
            <div className="ref-box">
              <span style={{fontSize:11,color:T.smoke,display:"block",marginBottom:3}}>RÉFÉRENCE(S) RÉSERVATION</span>
              {bData.reference}{isRT && returnBData && <> · {returnBData.reference}</>}
            </div>

            {operator === "CASH" ? (
              <>
                <div className="cash-receipt">
                  <div className="cash-receipt-label">Montant à encaisser</div>
                  <div className="cash-receipt-amount">{fmtPrice(totalAmount)}</div>
                  <div className="cash-receipt-sub">CASH · Guichet SETRAG</div>
                </div>
                <div className="pay-info">
                  <div className="pay-info-title" style={{display:"flex",alignItems:"center",gap:7}}><MoneyIcon size={15}/> Procédure encaissement espèces</div>
                  <ol className="pay-steps-list">
                    {OPERATORS_ALL.find(o=>o.id==="CASH").steps.map((s,i)=>(
                      <li key={i} className="pay-step"><span className="pay-num">{i+1}</span><span>{s}</span></li>
                    ))}
                  </ol>
                  <div className="pay-tip">Montant exact : <strong style={{color:T.forest,fontSize:14}}>{fmtPrice(totalAmount)}</strong></div>
                </div>
                <button className="btn btn-gold btn-full" onClick={handlePay} disabled={loading}>
                  {loading?"Confirmation…":"Confirmer encaissement - "+fmtPrice(totalAmount)}
                </button>
              </>
            ) : (
              <>
                <div className="pay-info">
                  <div className="pay-info-title">{OPERATORS_ALL.find(o=>o.id===operator)?.icon} Instructions {OPERATORS_ALL.find(o=>o.id===operator)?.name}</div>
                  <ol className="pay-steps-list">
                    {OPERATORS_ALL.find(o=>o.id===operator)?.steps.map((s,i)=>(
                      <li key={i} className="pay-step"><span className="pay-num">{i+1}</span><span>{s}</span></li>
                    ))}
                  </ol>
                  <div className="pay-tip">Montant : <strong style={{color:T.forest}}>{fmtPrice(totalAmount)}</strong> (couvre {isRT?"les deux billets":"le billet"})</div>
                </div>
                <div>
                  <label className="dlabel">Référence de paiement reçue par SMS *</label>
                  <div className="ref-input-wrap">
                    <span className="ref-input-icon"><MessageIcon size={14}/></span>
                    <input className="dinput ref-input" placeholder="Ex : TXN-ABC123456" value={payRef}
                      onChange={e => setPayRef(e.target.value)}
                      style={{fontFamily:"DM Mono,monospace",letterSpacing:".05em"}}
                    />
                  </div>
                  <div style={{fontSize:11,color:T.smoke,marginTop:5}}>
                    ℹ️ Demandez au passager la référence reçue après paiement {OPERATORS_ALL.find(o=>o.id===operator)?.name}.
                  </div>
                </div>
                <button className="btn btn-primary btn-full" onClick={handlePay} disabled={loading || !payRef.trim()}>
                  {loading?"Vérification…":"Valider la référence & Confirmer"}
                </button>
              </>
            )}
          </>}

          {step===3 && (
            <div className="done-box">
              <div className="alert alert-success text-center" role="alert">
                
                <strong>Votre achat a été effectué avec succès !</strong>
              </div>
              <div className="done-ref">Réf : <strong style={{fontFamily:"monospace",color:T.forest}}>{bData.reference}</strong>{isRT && returnBData && <> · <strong style={{fontFamily:"monospace",color:T.forest}}>{returnBData.reference}</strong></>}</div>

              <TicketPreviewAgent trip={trip} passenger={passenger} seat={seat} bData={bData} label={isRT?"→ Billet Aller":null} />
              {isRT && returnBData && (
                <TicketPreviewAgent trip={returnTrip} passenger={passenger} seat={returnSeat} bData={returnBData} label="↩ Billet Retour" />
              )}

              <button className="btn btn-gold btn-full" onClick={handleDownload} style={{marginTop:4}}>
                ⬇️ Télécharger le(s) billet(s) (PDF)
              </button>
              <button className="btn btn-ghost btn-full" style={{marginTop:4}} onClick={onClose}>Fermer</button>
            </div>
          )}
        </div>
      </div>

      {/* Modale de confirmation Bootstrap - remplace window.confirm() */}
      {showConfirm && (
        <>
          <div className="modal-backdrop show" style={{zIndex:2000}}/>
          <div className="modal d-block" tabIndex="-1" style={{zIndex:2001}}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirmer l'achat</h5>
                  <button type="button" className="btn-close" onClick={()=>setShowConfirm(false)}/>
                </div>
                <div className="modal-body">
                  <p>Émettre {isRT ? "les billets aller-retour" : "le billet"} pour <strong>{passenger}</strong> ?</p>
                  <ul className="list-unstyled small text-muted mb-0">
                    <li>→ {trip.departure?.name||trip.departure} → {trip.arrival?.name||trip.arrival} · {trip.date} {seat?`· Siège ${seat}`:""}</li>
                    {isRT && <li>↩ {returnTrip.departure?.name||returnTrip.departure} → {returnTrip.arrival?.name||returnTrip.arrival} · {returnTrip.date} {returnSeat?`· Siège ${returnSeat}`:""}</li>}
                  </ul>
                  <p className="mt-2 mb-0">Montant total : <strong>{fmtPrice(totalAmount)}</strong></p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={()=>setShowConfirm(false)}>Annuler</button>
                  <button type="button" className="btn btn-success" onClick={handleBook} disabled={loading}>
                    {loading ? "⏳ Traitement…" : "Confirmer l'achat"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export function TicketPreviewAgent({ trip, passenger, seat, bData, label }) {
  const cls = CLASS_MAP[trip.travelClass] || CLASS_MAP.ECONOMIQUE;
  return (
    <div className="tp">
      <div className="tp-head">
        <span style={{fontWeight:800,color:T.gold,display:"inline-flex",alignItems:"center",gap:6}}><TrainIcon size={16}/> SETRAG {label && <span style={{fontSize:11,fontWeight:600,opacity:.8}}>- {label}</span>}</span>
        <span style={{fontFamily:"monospace",fontSize:11,color:T.smoke}}>{bData.reference}</span>
      </div>
      <div className="tp-route">
        {trip.departure?.name||trip.departure}
        <span className="tp-arrow">→</span>
        {trip.arrival?.name||trip.arrival}
      </div>
      <div className="tp-details">
        <span style={{display:"inline-flex",alignItems:"center",gap:4}}><ClockIcon size={12}/> {trip.departureTime} – {trip.arrivalTime}</span>
        <span>{trip.trainNumber}</span>
        <span>{cls.icon} {cls.label}</span>
        {seat&&<span style={{display:"inline-flex",alignItems:"center",gap:4}}><SeatIcon2 size={12}/> Siège {seat}</span>}
      </div>
      <div className="tp-footer">
        <span>{passenger||"-"}</span>
        <span style={{fontWeight:700,color:T.gold}}>{fmtPrice(trip.price)}</span>
      </div>
      {bData.qrPayload && (
        <div style={{display:"flex",justifyContent:"center",marginTop:10}}>
          <AgentTicketQR data={bData.qrPayload} size={88} />
        </div>
      )}
    </div>
  );
}

/* ── TRIP FORM DRAWER - Admin & Programmateur ── */
export function TripFormDrawer({ trip, onClose, onSaved, showToast }) {
  const isEdit = !!trip;
  const [f, setF] = useState({
    trainNumber:trip?.trainNumber||"", travelClass:trip?.travelClass||"ECONOMIQUE",
    departure:trip?.departure||"", arrival:trip?.arrival||"",
    date:trip?.date||"", departureTime:trip?.departureTime||"",
    arrivalTime:trip?.arrivalTime||"", price:trip?.price||"", totalSeats:trip?.totalSeats||"",
  });
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const set = (k,v) => setF(p=>({...p,[k]:v}));

  useEffect(()=>{document.body.style.overflow="hidden";return()=>{document.body.style.overflow="";};}, []);

  const handleSave = async () => {
    if (!f.trainNumber||!f.departure||!f.arrival||!f.date||!f.price){showToast("Champs obligatoires manquants","warning");return;}
    setLoading(true);
    try {
      if(isEdit) await api.put(`/api/admin/trips/${trip.id}`,f);
      else await api.post("/api/admin/trips",f);
      showToast(isEdit?"Trajet modifié":"Trajet créé","success");
      onSaved(); onClose();
    } catch(e){showToast(e?.response?.data?.error??e?.message??"Erreur","error");}
    finally{setLoading(false);}
  };

  const handleDelete = async () => {
    setShowDeleteConfirm(false);
    setLoading(true);
    try{await api.delete(`/api/admin/trips/${trip.id}`);showToast("Trajet supprimé","success");onSaved();onClose();}
    catch(e){showToast(e?.response?.data?.error??e?.message??"Erreur","error");}
    finally{setLoading(false);}
  };

  return (
    <>
      <div className="overlay" onClick={onClose}/>
      <div className="drawer">
        <div className="dhead">
          <button className="dclose" onClick={onClose}>←</button>
          <div className="dtitle" style={{display:"flex",alignItems:"center",gap:8}}>{isEdit?<EditIcon size={16}/>:<PlusIcon size={16}/>}{isEdit?"Modifier le trajet":"Nouveau trajet"}</div>
        </div>
        <div className="dbody">
          <div className="fg2">
            <div><label className="dlabel">N° Train *</label><input className="dinput" placeholder="Ex : T-105" value={f.trainNumber} onChange={e=>set("trainNumber",e.target.value)}/></div>
            <div><label className="dlabel">Classe *</label>
              <select className="dselect" value={f.travelClass} onChange={e=>set("travelClass",e.target.value)}>
                {TRAVEL_CLASSES.map(c=><option key={c} value={c}>{CLASS_MAP[c].label}</option>)}
              </select>
            </div>
          </div>
          <div className="fg2">
            <div><label className="dlabel">Gare départ *</label><div className="field"><AcInput value={f.departure} onChange={v=>set("departure",v)} placeholder="Libreville"/></div></div>
            <div><label className="dlabel">Gare arrivée *</label><div className="field"><AcInput value={f.arrival} onChange={v=>set("arrival",v)} placeholder="Franceville"/></div></div>
          </div>
          <div><label className="dlabel">Date *</label><input className="dinput" type="date" value={f.date} onChange={e=>set("date",e.target.value)}/></div>
          <div className="fg2">
            <div><label className="dlabel">Heure départ</label><input className="dinput" type="time" value={f.departureTime} onChange={e=>set("departureTime",e.target.value)}/></div>
            <div><label className="dlabel">Heure arrivée</label><input className="dinput" type="time" value={f.arrivalTime} onChange={e=>set("arrivalTime",e.target.value)}/></div>
          </div>
          <div className="fg2">
            <div><label className="dlabel">Prix (FCFA) *</label><input className="dinput" type="number" placeholder="15000" value={f.price} onChange={e=>set("price",e.target.value)}/></div>
            <div><label className="dlabel">Places totales</label><input className="dinput" type="number" placeholder="80" value={f.totalSeats} onChange={e=>set("totalSeats",e.target.value)}/></div>
          </div>
          <button className="btn btn-primary btn-full" onClick={handleSave} disabled={loading}>
            {loading?"Enregistrement…":isEdit?"Enregistrer":"Créer le trajet"}
          </button>
          {isEdit&&<button className="btn btn-danger btn-full" style={{marginTop:0,display:"inline-flex",alignItems:"center",justifyContent:"center",gap:7}} onClick={()=>setShowDeleteConfirm(true)} disabled={loading}><TrashIcon size={14}/> Supprimer ce trajet</button>}
          <button className="btn btn-ghost btn-full" onClick={onClose} style={{marginTop:0}}>Annuler</button>
        </div>
      </div>

      {showDeleteConfirm && (
        <>
          <div className="modal-backdrop show" style={{zIndex:2000}}/>
          <div className="modal d-block" tabIndex="-1" style={{zIndex:2001}}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Supprimer ce trajet ?</h5>
                  <button type="button" className="btn-close" onClick={()=>setShowDeleteConfirm(false)}/>
                </div>
                <div className="modal-body">
                  <p className="mb-0">Cette action est définitive et supprimera le trajet <strong>{f.trainNumber}</strong> ({f.departure} → {f.arrival}).</p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={()=>setShowDeleteConfirm(false)}>Annuler</button>
                  <button type="button" className="btn btn-danger" onClick={handleDelete} disabled={loading}>
                    {loading ? "⏳ Suppression…" : "Supprimer définitivement"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

/* ════════════════════════════════════════════════════
   ADMIN VIEW
════════════════════════════════════════════════════ */
export function AdminView({ activePage, showToast }) {
  const [stats, setStats]         = useState(null);
  const [recentBk, setRecentBk]   = useState([]);
  const [allBk, setAllBk]         = useState([]);
  const [allUsers, setAllUsers]   = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [revenue, setRevenue]     = useState([]);
  const [bkPage, setBkPage]       = useState(1);
  const [bkTotal, setBkTotal]     = useState(0);
  const [bkFilter, setBkFilter]   = useState("ALL");
  const [tripDrawer, setTripDrawer] = useState(null);
  const [printBk, setPrintBk]     = useState(null);

  const loadStats     = useCallback(()=>{ api.get("/api/admin/stats").then(r=>setStats(r.data)).catch(()=>{}); },[]);
  const loadRecent    = useCallback(()=>{ api.get("/api/admin/recent-bookings").then(r=>setRecentBk(Array.isArray(r.data)?r.data:[])).catch(()=>{}); },[]);
  const loadAllBk     = useCallback(()=>{ api.get("/api/admin/bookings",{params:{page:bkPage}}).then(r=>{ setAllBk(r.data.data||[]); setBkTotal(r.data.total||0); }).catch(()=>{}); },[bkPage]);
  const loadUsers     = useCallback(()=>{ api.get("/api/admin/users").then(r=>setAllUsers(Array.isArray(r.data)?r.data:[])).catch(()=>{}); },[]);
  const loadSchedules = useCallback(()=>{ api.get("/api/admin/schedules").then(r=>setSchedules(Array.isArray(r.data)?r.data:[])).catch(()=>{}); },[]);
  const loadRevenue   = useCallback(()=>{ api.get("/api/admin/revenue-chart").then(r=>setRevenue(Array.isArray(r.data)?r.data:[])).catch(()=>{}); },[]);

  useEffect(()=>{ loadStats(); loadRecent(); },[loadStats, loadRecent]);
  useEffect(()=>{ if(activePage==="bookings")  loadAllBk();     },[activePage,bkPage, loadAllBk]);
  useEffect(()=>{ if(activePage==="users")     loadUsers();     },[activePage, loadUsers]);
  useEffect(()=>{ if(activePage==="schedules") loadSchedules(); },[activePage, loadSchedules]);
  useEffect(()=>{ if(activePage==="revenue")   { loadRevenue(); loadStats(); } },[activePage, loadRevenue, loadStats]);

  const totalPages = Math.ceil(bkTotal/15);
  const filteredBk = bkFilter==="ALL" ? allBk : allBk.filter(b=>b.status===bkFilter);

  const chartData = (() => {
    const map={};
    revenue.forEach(r=>{ map[r.date]=(map[r.date]||0)+Number(r.amount||0); });
    return Object.entries(map).slice(-10).map(([date,amt])=>({date:date.slice(5),amt}));
  })();
  const maxAmt=Math.max(...chartData.map(d=>d.amt),1);

  if (activePage==="overview") return (
    <>
      <div className="rev-hero">
        <div>
          <div className="rev-label">Recettes totales SETRAG</div>
          <div className="rev-amount">{Number(stats?.revenue||0).toLocaleString("fr-FR")} FCFA</div>
          <div className="rev-sub">Chiffre d'affaires consolidé · Toutes lignes</div>
          <div className="rev-badges">
            <span className="rev-badge" style={{display:"inline-flex",alignItems:"center",gap:5}}><TicketIcon size={13}/> {stats?.bookings||"-"} réservations</span>
            <span className="rev-badge" style={{display:"inline-flex",alignItems:"center",gap:5}}><TrainIcon size={13}/> {stats?.trips||"-"} trajets</span>
            <span className="rev-badge" style={{display:"inline-flex",alignItems:"center",gap:5}}><UsersIcon size={13}/> {stats?.clients||"-"} voyageurs</span>
          </div>
        </div>
        <div style={{opacity:.2}}><MoneyIcon size={62}/></div>
      </div>
      <div className="kpi-grid">
        {[
          {l:"Voyageurs inscrits",v:stats?.clients||"-",icon:"👥",color:T.forest},
          {l:"Total réservations",v:stats?.bookings||"-",icon:"🎫",color:T.gold},
          {l:"Trajets actifs",v:stats?.trips||"-",icon:"🚆",color:T.blue},
          {l:"Recettes FCFA",v:Number(stats?.revenue||0).toLocaleString("fr-FR"),icon:"💰",color:T.rust},
        ].map((k,i)=>(
          <div className="kpi-card" key={i} data-icon={k.icon} style={{animationDelay:`${i*.07}s`}}>
            <div className="kpi-label">{k.l}</div>
            <div className="kpi-val" style={{color:k.color,fontSize:k.l==="Recettes FCFA"?"18px":undefined}}>{k.v}</div>
          </div>
        ))}
      </div>
      <div className="section">
        <div className="sec-head"><div className="sec-title">Réservations récentes</div><span className="sec-badge">{recentBk.length} entrées</span></div>
        <div className="tbl-wrap" style={{overflowX:"auto"}}>
          <table className="data-table">
            <thead><tr><th>Référence</th><th>Client</th><th>Trajet</th><th>Montant</th><th>Statut</th><th>Billet</th></tr></thead>
            <tbody>
              {recentBk.map(b=>{ const st=STATUS[b.status]||STATUS.PENDING; return(
                <tr key={b.id}>
                  <td><span className="mono">{b.reference}</span></td>
                  <td style={{fontWeight:600}}>{b.fullName}</td>
                  <td>{b.route}<br/><span style={{fontSize:11,color:T.smoke}}>{b.trainNumber}</span></td>
                  <td style={{fontFamily:"Playfair Display,serif",fontWeight:700,color:T.forest,whiteSpace:"nowrap"}}>{fmtPrice(b.amount)}</td>
                  <td><span className="status-pill" style={{background:st.bg,color:st.color}}>{st.label}</span></td>
                  <td><button className="btn btn-print btn-sm" onClick={()=>setPrintBk(b)}><PrinterIcon size={14}/></button></td>
                </tr>
              );})}
              {!recentBk.length&&<tr><td colSpan={6} style={{textAlign:"center",color:T.smoke,padding:28}}>Aucune réservation</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {printBk && <PrintTicketModal booking={printBk} onClose={()=>setPrintBk(null)}/>}
    </>
  );

  if (activePage==="bookings") return (
    <div className="section" style={{animation:"fadeUp .4s ease both"}}>
      <div className="sec-head"><div className="sec-title">Toutes les réservations</div><span className="sec-badge">{bkTotal} au total</span></div>
      <div className="filter-row">
        {["ALL","CONFIRMED","PENDING","CANCELLED"].map(f=>(
          <button key={f} className={`filter-btn${bkFilter===f?" active":""}`} onClick={()=>{setBkFilter(f);setBkPage(1);}}>
            {f==="ALL"?"Toutes":STATUS[f]?.label}
          </button>
        ))}
      </div>
      <div className="tbl-wrap" style={{overflowX:"auto"}}>
        <table className="data-table">
          <thead><tr><th>Référence</th><th>Client</th><th>Trajet</th><th>Classe</th><th>Date</th><th>Montant</th><th>Statut</th><th>Billet</th></tr></thead>
          <tbody>
            {filteredBk.map(b=>{ const st=STATUS[b.status]||STATUS.PENDING; const cls=CLASS_MAP[b.travelClass]||CLASS_MAP.ECONOMIQUE; return(
              <tr key={b.id}>
                <td><span className="mono">{b.reference}</span></td>
                <td style={{fontWeight:600}}>{b.fullName}</td>
                <td>{b.route}<br/><span style={{fontSize:11,color:T.smoke}}>{b.trainNumber}</span></td>
                <td>{cls.icon} {cls.label}</td>
                <td className="mono" style={{fontSize:12}}>{b.date?.split(" ")[0]||"-"}</td>
                <td style={{fontFamily:"Playfair Display,serif",fontWeight:700,color:T.forest,whiteSpace:"nowrap"}}>{fmtPrice(b.amount)}</td>
                <td><span className="status-pill" style={{background:st.bg,color:st.color}}>{st.label}</span></td>
                <td><button className="btn btn-print btn-sm" onClick={()=>setPrintBk(b)}><PrinterIcon size={14}/></button></td>
              </tr>
            );})}
            {!filteredBk.length&&<tr><td colSpan={8} style={{textAlign:"center",color:T.smoke,padding:28}}>Aucune réservation</td></tr>}
          </tbody>
        </table>
      </div>
      {totalPages>1&&<div className="pager">
        <button className="pager-btn" onClick={()=>setBkPage(p=>p-1)} disabled={bkPage===1}>←</button>
        {Array.from({length:Math.min(totalPages,7)},(_,i)=>i+1).map(p=>(
          <button key={p} className={`pager-btn${bkPage===p?" active":""}`} onClick={()=>setBkPage(p)}>{p}</button>
        ))}
        <button className="pager-btn" onClick={()=>setBkPage(p=>p+1)} disabled={bkPage===totalPages}>→</button>
      </div>}
      {printBk && <PrintTicketModal booking={printBk} onClose={()=>setPrintBk(null)}/>}
    </div>
  );

  if (activePage==="schedules") return (
    <div className="section" style={{animation:"fadeUp .4s ease both"}}>
      <div className="sec-head">
        <div className="sec-title">Gestion des horaires</div>
        <div className="sec-actions">
          <button className="btn btn-ghost btn-sm" onClick={loadSchedules} style={{display:"inline-flex",alignItems:"center",gap:6}}><RefreshIcon size={14}/> Actualiser</button>
          <button className="btn btn-primary btn-sm" onClick={()=>setTripDrawer(false)} style={{display:"inline-flex",alignItems:"center",gap:6}}><PlusIcon size={14}/> Nouveau trajet</button>
        </div>
      </div>
      <div className="tbl-wrap" style={{overflowX:"auto"}}>
        <table className="data-table">
          <thead><tr><th>Train</th><th>Trajet</th><th>Date</th><th>Horaires</th><th>Classe</th><th>Prix</th><th>Places</th><th>Résas</th><th>Actions</th></tr></thead>
          <tbody>
            {schedules.map(t=>{ const cls=CLASS_MAP[t.travelClass]||CLASS_MAP.ECONOMIQUE; const pct=t.totalSeats?Math.round(((t.totalSeats-t.seats)/t.totalSeats)*100):0; return(
              <tr key={t.id}>
                <td><span className="mono" style={{fontWeight:600}}>{t.trainNumber}</span></td>
                <td style={{fontWeight:600}}>{t.departure}<span style={{color:T.smoke,margin:"0 4px"}}>→</span>{t.arrival}</td>
                <td className="mono" style={{fontSize:12}}>{t.date||"-"}</td>
                <td className="mono" style={{fontSize:12,whiteSpace:"nowrap"}}>{t.departureTime} → {t.arrivalTime}</td>
                <td>{cls.icon} {cls.label}</td>
                <td style={{fontFamily:"Playfair Display,serif",fontWeight:700,color:T.forest,whiteSpace:"nowrap"}}>{fmtPrice(t.price)}</td>
                <td>
                  <span style={{fontWeight:700,color:t.seats<=5?T.rust:T.forest}}>{t.seats}</span>
                  <span style={{color:T.smoke,fontSize:12}}>/{t.totalSeats||"-"}</span>
                  {t.totalSeats>0&&<div className="fill-bar"><div className="fill-inner" style={{width:`${Math.min(pct,100)}%`,background:pct>80?T.rust:T.forest}}/></div>}
                </td>
                <td style={{fontWeight:700,color:T.forest,textAlign:"center"}}>{t.bookingsCount||0}</td>
                <td><button className="btn btn-ghost btn-sm" onClick={()=>setTripDrawer(t)}><EditIcon size={14}/></button></td>
              </tr>
            );})}
            {!schedules.length&&<tr><td colSpan={9} style={{textAlign:"center",color:T.smoke,padding:28}}>Aucun trajet</td></tr>}
          </tbody>
        </table>
      </div>
      {tripDrawer!==null&&<TripFormDrawer trip={tripDrawer||null} onClose={()=>setTripDrawer(null)} onSaved={loadSchedules} showToast={showToast}/>}
    </div>
  );

  if (activePage==="users") return (
    <div className="section" style={{animation:"fadeUp .4s ease both"}}>
      <div className="sec-head"><div className="sec-title">Utilisateurs</div><span className="sec-badge">{allUsers.length} inscrits</span></div>
      <div className="tbl-wrap" style={{overflowX:"auto"}}>
        <table className="data-table">
          <thead><tr><th>#</th><th>Nom</th><th>Email</th><th>Téléphone</th><th>Rôles</th></tr></thead>
          <tbody>
            {allUsers.map((u,i)=>(
              <tr key={u.id}>
                <td className="mono" style={{color:T.smoke}}>{i+1}</td>
                <td style={{fontWeight:700}}>{u.name}</td>
                <td className="mono" style={{fontSize:12}}>{u.email}</td>
                <td className="mono" style={{fontSize:12}}>{u.phone||"-"}</td>
                <td>{(u.roles||[]).map(r=><span key={r} className="badge badge-cls" style={{fontSize:10}}>{r.replace("ROLE_","")}</span>)}</td>
              </tr>
            ))}
            {!allUsers.length&&<tr><td colSpan={5} style={{textAlign:"center",color:T.smoke,padding:28}}>Aucun utilisateur</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );

  if (activePage==="revenue") return (
    <>
      <div className="rev-hero">
        <div>
          <div className="rev-label">Total recettes SETRAG</div>
          <div className="rev-amount">{Number(stats?.revenue||0).toLocaleString("fr-FR")} FCFA</div>
          <div className="rev-sub">Paiements confirmés uniquement · Toutes classes</div>
        </div>
        <div style={{opacity:.2}}><MoneyIcon size={54}/></div>
      </div>
      <div className="section">
        <div className="sec-head"><div className="sec-title">Évolution (10 derniers jours)</div></div>
        <div style={{background:T.white,borderRadius:13,border:`1.5px solid ${T.creamDk}`,padding:"22px 18px"}}>
          {chartData.length===0
            ? <div style={{textAlign:"center",color:T.smoke,padding:28}}>Aucune donnée financière</div>
            : <>
                <div className="bar-chart">
                  {chartData.map((d,i)=>(
                    <div key={i} className="bar-wrap">
                      <div className="bar-v">{(d.amt/1000).toFixed(0)}k</div>
                      <div className="bar" style={{height:`${Math.max((d.amt/maxAmt)*88,6)}px`,background:`linear-gradient(to top,${T.forest},${T.forestMid})`}}/>
                      <div className="bar-lbl">{d.date}</div>
                    </div>
                  ))}
                </div>
                <div style={{marginTop:12,fontSize:11,color:T.smoke,textAlign:"right"}}>Montants en milliers de FCFA</div>
              </>
          }
        </div>
      </div>
      <div className="section">
        <div className="sec-head"><div className="sec-title">Détail transactions</div></div>
        <div className="tbl-wrap" style={{overflowX:"auto"}}>
          <table className="data-table">
            <thead><tr><th>Date</th><th>Classe</th><th>Montant</th></tr></thead>
            <tbody>
              {revenue.slice(0,50).map((r,i)=>{ const cls=CLASS_MAP[r.class]||CLASS_MAP.ECONOMIQUE; return(
                <tr key={i}>
                  <td className="mono" style={{fontSize:12}}>{r.date}</td>
                  <td>{cls.icon} {cls.label}</td>
                  <td style={{fontFamily:"Playfair Display,serif",fontWeight:700,color:T.forest}}>{fmtPrice(r.amount)}</td>
                </tr>
              );})}
              {!revenue.length&&<tr><td colSpan={3} style={{textAlign:"center",color:T.smoke,padding:28}}>Aucune transaction</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );

  return null;
}

/* ════════════════════════════════════════════════════
   AGENT VIEW - filtrage trajets valides + billet imprimable
════════════════════════════════════════════════════ */
export function AgentView({ activePage, showToast }) {
  const [dep, setDep]   = useState("");
  const [arr, setArr]   = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [showExpired, setShowExpired] = useState(false);  // filtre trajets valides
  const [trips, setTrips]       = useState([]);
  const [searched, setSearched] = useState(false);
  const [searching, setSearching] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [bkFilter, setBkFilter] = useState("ALL");
  const [selTrip, setSelTrip]   = useState(null);
  const [printBk, setPrintBk]   = useState(null);
  const [, startT] = useTransition();

  // Aller-retour
  const [roundTrip, setRoundTrip]     = useState(false);
  const [returnDate, setReturnDate]   = useState("");
  const [returnTrips, setReturnTrips] = useState([]);
  const [selOutbound, setSelOutbound] = useState(null); // trajet aller choisi (mode aller-retour)
  const [selReturn, setSelReturn]     = useState(null); // trajet retour choisi (mode aller-retour)

  const loadBk = useCallback(async()=>{
    try{ const r=await api.get("/api/admin/recent-bookings"); setBookings(Array.isArray(r.data)?r.data:[]); }
    catch{ showToast("Erreur chargement réservations","error"); }
  },[showToast]);

  useEffect(()=>{ if(activePage==="bookings") loadBk(); },[activePage, loadBk]);

  const handleSearch = async () => {
    if(!dep||!arr){showToast("Départ et arrivée requis","warning");return;}
    if(roundTrip && !returnDate){showToast("Choisissez une date de retour","warning");return;}
    setSearching(true);
    setSelOutbound(null); setSelReturn(null);
    try{
      const r=await api.get("/trips/search",{params:{departure:dep,destination:arr,date}});
      setTrips(Array.isArray(r.data)?r.data:[]);
      if (roundTrip) {
        const r2 = await api.get("/trips/search",{params:{departure:arr,destination:dep,date:returnDate}});
        setReturnTrips(Array.isArray(r2.data)?r2.data:[]);
      } else {
        setReturnTrips([]);
      }
      setSearched(true);
    }
    catch{ showToast("Erreur de recherche","error"); }
    finally{ setSearching(false); }
  };

  // Filtrer selon validité et préférence de l'agent
  const displayedTrips = trips.filter(t => showExpired ? true : isTripValid(t));
  const expiredCount   = trips.filter(t => !isTripValid(t)).length;

  const filtered = bkFilter==="ALL" ? bookings : bookings.filter(b=>b.status===bkFilter);

  if (activePage==="search") return (
    <>
      <div className="agent-hero">
        <div>
          <div style={{fontSize:11,color:"rgba(255,255,255,.45)",textTransform:"uppercase",letterSpacing:".12em",marginBottom:8}}>Guichet SETRAG</div>
          <div style={{fontFamily:"Playfair Display,serif",fontSize:"clamp(20px,4vw,30px)",fontWeight:900,color:"#fff",lineHeight:1}}>Émission de billets</div>
          <div style={{fontSize:13,color:"rgba(255,255,255,.55)",marginTop:8}}>Recherchez un trajet et émettez le billet pour le passager</div>
        </div>
        <div style={{opacity:.2}}><TicketIcon size={56}/></div>
      </div>

      <div className="section">
        <div className="sec-head"><div className="sec-title">Rechercher un trajet</div></div>
        <div className="search-wrap">
          <div className="btn-group mb-3" role="group" aria-label="Type de trajet">
            <button type="button" className={`btn btn-sm ${!roundTrip ? "btn-success" : "btn-outline-success"}`} onClick={()=>setRoundTrip(false)}>Aller simple</button>
            <button type="button" className={`btn btn-sm ${roundTrip ? "btn-success" : "btn-outline-success"}`} onClick={()=>setRoundTrip(true)}>Aller-retour</button>
          </div>
          <div className="search-grid">
            <div className="field"><label>Départ</label><AcInput value={dep} onChange={setDep} placeholder="Libreville"/></div>
            <div className="field"><label>Arrivée</label><AcInput value={arr} onChange={setArr} placeholder="Franceville"/></div>
            <div className="field"><label>Date aller</label><input type="date" value={date} onChange={e=>{setDate(e.target.value); if(returnDate && returnDate<e.target.value) setReturnDate(e.target.value);}}/></div>
            {roundTrip && (
              <div className="field"><label>Date retour</label><input type="date" value={returnDate} min={date} onChange={e=>setReturnDate(e.target.value)}/></div>
            )}
            <div className="field"><label style={{opacity:0}}>.</label>
              <button className="btn btn-primary" style={{width:"100%",padding:"10px 14px"}} onClick={handleSearch} disabled={searching}>
                {searching?"Recherche…":<><SearchIcon size={14}/> Rechercher</>}
              </button>
            </div>
          </div>
        </div>
      </div>

      {searched&&(
        <div className="section">
          <div className="sec-head">
            <div className="sec-title">Trajets disponibles</div>
            <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
              <span className="sec-badge">{displayedTrips.length} résultat{displayedTrips.length!==1?"s":""}</span>
              {expiredCount > 0 && (
                <button
                  className={`filter-btn${showExpired?" active":""}`}
                  onClick={()=>setShowExpired(p=>!p)}
                  style={{fontSize:11}}
                >
                  {showExpired ? "Tous les trajets" : `Afficher ${expiredCount} trajet${expiredCount>1?"s":""} expiré${expiredCount>1?"s":""}`}
                </button>
              )}
            </div>
          </div>

          {!showExpired && expiredCount > 0 && (
            <div style={{background:"#fef3c7",border:"1px solid #f59e0b",borderRadius:10,padding:"10px 14px",marginBottom:13,display:"flex",alignItems:"center",gap:9,fontSize:13}}>
              <span>⏰</span>
              <span style={{color:"#92400e"}}>
                <strong>{expiredCount} trajet{expiredCount>1?"s":""} déjà parti{expiredCount>1?"s":""}</strong> masqué{expiredCount>1?"s":""}. Seuls les trajets à venir sont affichés.
              </span>
            </div>
          )}

          {displayedTrips.length===0
            ? <div className="empty"><div className="empty-icon"><SearchIcon size={28}/></div>
                <p className="empty-text">
                  {trips.length > 0 ? "Tous les trajets trouvés sont déjà partis." : "Aucun trajet trouvé."}<br/>
                  {trips.length > 0
                    ? <button className="filter-btn" style={{marginTop:10}} onClick={()=>setShowExpired(true)}>Afficher quand même</button>
                    : "Essayez d'autres dates ou destinations."
                  }
                </p>
              </div>
            : <div className="trips-list">
                {displayedTrips.map((t,i)=>{
                  const cls=CLASS_MAP[t.travelClass]||CLASS_MAP.ECONOMIQUE;
                  const avail=t.seats??t.availableSeats;
                  const isLow=avail<=5;
                  const isExpired=!isTripValid(t);
                  const isChosen = roundTrip && selOutbound?.id === t.id;
                  const pick = () => {
                    if (isExpired) return;
                    if (roundTrip) startT(()=>setSelOutbound(t));
                    else startT(()=>setSelTrip(t));
                  };
                  return(
                    <div className="trip-card" key={t.id}
                      style={{animationDelay:`${i*.06}s`,opacity:isExpired?.55:1,borderColor:isChosen?T.forest:isExpired?"#fca5a5":undefined, borderWidth:isChosen?2:undefined}}
                      onClick={pick}
                    >
                      <div>
                        <div className="st-time" style={{color:isExpired?T.smoke:undefined}}>{t.departureTime}</div>
                        <div className="st-name">{t.departure?.name||t.departure}</div>
                        {isExpired&&<div style={{fontSize:10,color:T.rust,fontWeight:700,marginTop:3}}>⏰ Trajet passé</div>}
                      </div>
                      <div className="trip-mid">
                        <div className="trip-line"><div className="trip-dot"/><div className="trip-dash"/><div className="trip-dot"/></div>
                        <div className="trip-dur">⏱ {t.duration||"-"}</div>
                      </div>
                      <div><div className="st-time">{t.arrivalTime}</div><div className="st-name">{t.arrival?.name||t.arrival}</div></div>
                      <div>
                        <span className="badge badge-cls">{cls.icon} {cls.label}</span><br/>
                        <span className="badge" style={{background:isLow?"rgba(139,58,26,.1)":"rgba(200,148,42,.12)",color:isLow?T.rust:"#a07320"}}>
                          {avail??"?"} places
                        </span>
                      </div>
                      <div style={{textAlign:"right"}}>
                        <div className="trip-price-val">{Number(t.price).toLocaleString("fr-FR")}</div>
                        <div className="trip-price-cur">FCFA / pers.</div>
                        {!isExpired&&(
                          <button className={`btn btn-sm ${isChosen?"btn-success":"btn-primary"}`} style={{marginTop:8}}
                            onClick={e=>{e.stopPropagation();pick();}}>
                            {roundTrip ? (isChosen?"✓ Aller choisi":"Choisir cet aller") : "Émettre billet"}
                          </button>
                        )}
                        {isExpired&&(
                          <div style={{fontSize:11,color:T.rust,fontWeight:700,marginTop:8}}>Non disponible</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
          }
        </div>
      )}

      {roundTrip && searched && (
        <div className="section">
          <div className="sec-head">
            <div className="sec-title">Trajet retour {selOutbound ? "" : "(choisissez d'abord l'aller)"}</div>
            <span className="sec-badge">{returnTrips.length} résultat{returnTrips.length!==1?"s":""}</span>
          </div>
          {!selOutbound ? (
            <div className="empty"><div className="empty-icon">↩️</div><p className="empty-text">Sélectionnez d'abord un train aller ci-dessus.</p></div>
          ) : returnTrips.length===0 ? (
            <div className="empty"><div className="empty-icon"><SearchIcon size={28}/></div><p className="empty-text">Aucun trajet retour trouvé pour cette date.</p></div>
          ) : (
            <div className="trips-list">
              {returnTrips.map((t,i)=>{
                const cls=CLASS_MAP[t.travelClass]||CLASS_MAP.ECONOMIQUE;
                const avail=t.seats??t.availableSeats;
                const isChosen = selReturn?.id === t.id;
                return(
                  <div className="trip-card" key={t.id} style={{animationDelay:`${i*.06}s`, borderColor:isChosen?T.forest:undefined, borderWidth:isChosen?2:undefined}}
                    onClick={()=>startT(()=>setSelReturn(t))}>
                    <div><div className="st-time">{t.departureTime}</div><div className="st-name">{t.departure?.name||t.departure}</div></div>
                    <div className="trip-mid">
                      <div className="trip-line"><div className="trip-dot"/><div className="trip-dash"/><div className="trip-dot"/></div>
                      <div className="trip-dur">⏱ {t.duration||"-"}</div>
                    </div>
                    <div><div className="st-time">{t.arrivalTime}</div><div className="st-name">{t.arrival?.name||t.arrival}</div></div>
                    <div><span className="badge badge-cls">{cls.icon} {cls.label}</span><br/>
                      <span className="badge" style={{background:"rgba(200,148,42,.12)",color:"#a07320"}}>{avail??"?"} places</span>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div className="trip-price-val">{Number(t.price).toLocaleString("fr-FR")}</div>
                      <div className="trip-price-cur">FCFA / pers.</div>
                      <button className={`btn btn-sm ${isChosen?"btn-success":"btn-primary"}`} style={{marginTop:8}}
                        onClick={e=>{e.stopPropagation();startT(()=>setSelReturn(t));}}>
                        {isChosen?"✓ Retour choisi":"Choisir ce retour"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {roundTrip && selOutbound && selReturn && !selTrip && (
        <div style={{position:"sticky",bottom:16,zIndex:20,display:"flex",justifyContent:"center",marginTop:20}}>
          <button className="btn btn-success btn-lg shadow" style={{borderRadius:30,padding:"12px 32px",fontWeight:700}}
            onClick={()=>startT(()=>setSelTrip({ ...selOutbound, __isRoundTripPair:true }))}>
            Émettre les billets aller-retour
          </button>
        </div>
      )}

      {selTrip&&(
        <BookingDrawer
          trip={roundTrip ? selOutbound : selTrip}
          returnTrip={roundTrip ? selReturn : null}
          onClose={()=>startT(()=>{ setSelTrip(null); })}
          onConfirmed={()=>{ loadBk(); startT(()=>{ setSelTrip(null); setSelOutbound(null); setSelReturn(null); }); }}
          showToast={showToast}
        />
      )}
    </>
  );

  if (activePage==="bookings") return (
    <div className="section" style={{animation:"fadeUp .4s ease both"}}>
      <div className="agent-hero" style={{marginBottom:22}}>
        <div>
          <div style={{fontSize:11,color:"rgba(255,255,255,.45)",textTransform:"uppercase",letterSpacing:".12em",marginBottom:8}}>Guichet SETRAG</div>
          <div style={{fontFamily:"Playfair Display,serif",fontSize:"clamp(18px,3vw,26px)",fontWeight:900,color:"#fff"}}>Réservations du jour</div>
          <div style={{fontSize:13,color:"rgba(255,255,255,.55)",marginTop:8}}>Consultez et gérez les billets émis</div>
        </div>
        <div style={{opacity:.2}}><TicketIcon size={54}/></div>
      </div>
      <div className="sec-head">
        <div className="sec-title">Liste des réservations</div>
        <button className="btn btn-ghost btn-sm" onClick={loadBk} style={{display:"inline-flex",alignItems:"center",gap:6}}><RefreshIcon size={14}/> Actualiser</button>
      </div>
      <div className="filter-row">
        {["ALL","CONFIRMED","PENDING","CANCELLED"].map(f=>(
          <button key={f} className={`filter-btn${bkFilter===f?" active":""}`} onClick={()=>setBkFilter(f)}>
            {f==="ALL"?"Toutes":STATUS[f]?.label}
          </button>
        ))}
      </div>
      <div className="tbl-wrap" style={{overflowX:"auto"}}>
        <table className="data-table">
          <thead><tr><th>Référence</th><th>Passager</th><th>Trajet</th><th>Classe</th><th>Siège</th><th>Statut</th><th>Billet</th></tr></thead>
          <tbody>
            {filtered.map(b=>{ const st=STATUS[b.status]||STATUS.PENDING; const cls=CLASS_MAP[b.travelClass]||CLASS_MAP.ECONOMIQUE; return(
              <tr key={b.id}>
                <td><span className="mono">{b.reference}</span></td>
                <td style={{fontWeight:600}}>{b.fullName}</td>
                <td>{b.route}<br/><span style={{fontSize:11,color:T.smoke}}>{b.trainNumber}</span></td>
                <td>{cls.icon} {cls.label}</td>
                <td className="mono">{b.seatNumber?"Nº "+b.seatNumber:"Libre"}</td>
                <td><span className="status-pill" style={{background:st.bg,color:st.color}}>{st.label}</span></td>
                <td>
                  <button className="btn btn-print btn-sm" onClick={()=>setPrintBk(b)} title="Imprimer le billet">
                    <PrinterIcon size={13}/> Billet
                  </button>
                </td>
              </tr>
            );})}
            {!filtered.length&&<tr><td colSpan={7} style={{textAlign:"center",color:T.smoke,padding:28}}>Aucune réservation</td></tr>}
          </tbody>
        </table>
      </div>
      {printBk && <PrintTicketModal booking={printBk} onClose={()=>setPrintBk(null)}/>}
    </div>
  );

  return null;
}

/* ════════════════════════════════════════════════════
   PROGRAMMATEUR VIEW
════════════════════════════════════════════════════ */
/* ════════════════════════════════════════════════════
   CONTRÔLEUR - redirige vers la page de scan dédiée
════════════════════════════════════════════════════ */
export function ProgrammerView({ showToast }) {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading]     = useState(false);
  const [tripDrawer, setTripDrawer] = useState(null);
  const [filterClass, setFilterClass] = useState("ALL");
  const [search, setSearch]         = useState("");

  const load = useCallback(async()=>{
    setLoading(true);
    try{ const r=await api.get("/api/admin/schedules"); setSchedules(Array.isArray(r.data)?r.data:[]); }
    catch{ showToast("Erreur chargement trajets","error"); }
    finally{ setLoading(false); }
  },[showToast]);

  useEffect(()=>{ load(); },[load]);

  const filtered=schedules.filter(t=>{
    if(filterClass!=="ALL"&&t.travelClass!==filterClass) return false;
    if(search&&!`${t.trainNumber} ${t.departure} ${t.arrival}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const totalSeats  = schedules.reduce((s,t)=>(s+(t.totalSeats||0)),0);
  const totalResas  = schedules.reduce((s,t)=>(s+(t.bookingsCount||0)),0);

  return (
    <>
      <div className="prog-hero">
        <div>
          <div style={{fontSize:11,color:"rgba(255,255,255,.45)",textTransform:"uppercase",letterSpacing:".12em",marginBottom:8}}>Programmation SETRAG</div>
          <div style={{fontFamily:"Playfair Display,serif",fontSize:"clamp(20px,4vw,30px)",fontWeight:900,color:"#fff",lineHeight:1}}>Gestion des horaires</div>
          <div style={{display:"flex",gap:14,marginTop:12,flexWrap:"wrap"}}>
            <span style={{fontSize:12,color:"rgba(255,255,255,.65)",background:"rgba(255,255,255,.1)",padding:"4px 11px",borderRadius:50,display:"inline-flex",alignItems:"center",gap:5}}><TrainIcon size={12}/> {schedules.length} trajets</span>
            <span style={{fontSize:12,color:"rgba(255,255,255,.65)",background:"rgba(255,255,255,.1)",padding:"4px 11px",borderRadius:50,display:"inline-flex",alignItems:"center",gap:5}}><SeatIcon2 size={12}/> {totalSeats} places</span>
            <span style={{fontSize:12,color:"rgba(255,255,255,.65)",background:"rgba(255,255,255,.1)",padding:"4px 11px",borderRadius:50,display:"inline-flex",alignItems:"center",gap:5}}><TicketIcon size={12}/> {totalResas} réservations</span>
          </div>
        </div>
        <div style={{opacity:.2}}><CalendarIcon size={54}/></div>
      </div>

      <div className="section">
        <div className="sec-head">
          <div>
            <div className="sec-title">Horaires & Trajets</div>
            <div style={{fontSize:12,color:T.smoke,marginTop:2}}>{filtered.length} trajet{filtered.length!==1?"s":""} affiché{filtered.length!==1?"s":""}</div>
          </div>
          <div className="sec-actions">
            <button className="btn btn-ghost btn-sm" onClick={load}><RefreshIcon size={14}/></button>
            <button className="btn btn-primary" onClick={()=>setTripDrawer(false)} style={{display:"inline-flex",alignItems:"center",gap:6}}><PlusIcon size={14}/> Nouveau trajet</button>
          </div>
        </div>

        <div style={{display:"flex",gap:10,marginBottom:13,flexWrap:"wrap",alignItems:"center"}}>
          <input placeholder="Train, gare…" value={search} onChange={e=>setSearch(e.target.value)}
            style={{padding:"8px 12px",borderRadius:9,border:`1.5px solid ${T.creamDk}`,fontFamily:"DM Sans,sans-serif",fontSize:13,background:T.white,outline:"none",minWidth:190}}
          />
          <div className="filter-row" style={{margin:0}}>
            <button className={`filter-btn${filterClass==="ALL"?" active":""}`} onClick={()=>setFilterClass("ALL")}>Toutes classes</button>
            {TRAVEL_CLASSES.map(c=>(
              <button key={c} className={`filter-btn${filterClass===c?" active":""}`} onClick={()=>setFilterClass(c)}>{CLASS_MAP[c].icon} {CLASS_MAP[c].label}</button>
            ))}
          </div>
        </div>

        {loading
          ? <div style={{textAlign:"center",padding:44,color:T.smoke}}><div className="spinner" style={{display:"inline-block"}}/>Chargement…</div>
          : filtered.length===0
            ? <div className="empty"><div className="empty-icon"><TrainIcon size={32}/></div><p className="empty-text">Aucun trajet trouvé.<br/>Créez votre premier trajet avec le bouton ci-dessus.</p></div>
            : <div className="tbl-wrap" style={{overflowX:"auto"}}>
                <table className="data-table">
                  <thead><tr><th>Train</th><th>Trajet</th><th>Date</th><th>Départ → Arrivée</th><th>Classe</th><th>Prix</th><th>Places dispo</th><th>Résas</th><th>Action</th></tr></thead>
                  <tbody>
                    {filtered.map(t=>{ const cls=CLASS_MAP[t.travelClass]||CLASS_MAP.ECONOMIQUE; const pct=t.totalSeats?Math.round(((t.totalSeats-t.seats)/t.totalSeats)*100):0; return(
                      <tr key={t.id}>
                        <td><span className="mono" style={{fontWeight:700,color:T.forest}}>{t.trainNumber}</span></td>
                        <td style={{fontWeight:600}}>{t.departure}<span style={{color:T.smoke,margin:"0 4px"}}>→</span>{t.arrival}</td>
                        <td className="mono" style={{fontSize:12}}>{t.date||"-"}</td>
                        <td className="mono" style={{fontSize:12,whiteSpace:"nowrap"}}>{t.departureTime} → {t.arrivalTime}</td>
                        <td>{cls.icon} {cls.label}</td>
                        <td style={{fontFamily:"Playfair Display,serif",fontWeight:700,color:T.forest,whiteSpace:"nowrap"}}>{fmtPrice(t.price)}</td>
                        <td>
                          <div style={{display:"flex",alignItems:"center",gap:5}}>
                            <span style={{fontWeight:700,color:t.seats<=5?T.rust:T.forest}}>{t.seats}</span>
                            <span style={{color:T.smoke,fontSize:11}}>/ {t.totalSeats||"-"}</span>
                          </div>
                          {t.totalSeats>0&&<div className="fill-bar"><div className="fill-inner" style={{width:`${Math.min(pct,100)}%`,background:pct>80?T.rust:T.forest}}/></div>}
                          {t.seats<=5&&<span style={{fontSize:10,color:T.rust,fontWeight:700,display:"inline-flex",alignItems:"center",gap:3}}><AlertIcon size={11}/> Presque complet</span>}
                        </td>
                        <td style={{fontWeight:700,color:T.forest,textAlign:"center"}}>{t.bookingsCount||0}</td>
                        <td><button className="btn btn-ghost btn-sm" onClick={()=>setTripDrawer(t)} style={{display:"inline-flex",alignItems:"center",gap:5}}><EditIcon size={13}/> Modifier</button></td>
                      </tr>
                    );})}
                  </tbody>
                </table>
              </div>
        }
      </div>
      {tripDrawer!==null&&<TripFormDrawer trip={tripDrawer||null} onClose={()=>setTripDrawer(null)} onSaved={load} showToast={showToast}/>}
    </>
  );
}

/* ════════════════════════════════════════════════════
   NAV UNIFIÉE PAR RÔLE (pilotée par route, pas par onglet interne)
   Utilisée par TOUTES les pages de l'espace staff - Admin, Agent,
   Programmateur, Contrôleur, Support - pour un thème et une navigation
   cohérents partout (même police, mêmes couleurs, mêmes entrées de menu
   visibles selon le rôle, où qu'on se trouve dans l'espace staff).
════════════════════════════════════════════════════ */
export const DASHBOARD_PATH = {
  ROLE_ADMIN:      "/admin",
  ROLE_AGENT:      "/agent",
  ROLE_PROGRAMMER: "/programmer",
  ROLE_CONTROLLER: "/admin/scan",
  ROLE_SUPPORT:    "/admin/messages",
};

const STAFF_NAV = [
  { path: "/admin/messages", icon: <MessageIcon size={16}/>,  label: "Messagerie",       roles: ["ROLE_ADMIN", "ROLE_SUPPORT", "ROLE_AGENT", "ROLE_CONTROLLER", "ROLE_PROGRAMMER"], badge: true },
  { path: null,              icon: <GridIcon size={16}/>,     label: "Tableau de bord",  roles: ["ROLE_ADMIN", "ROLE_AGENT", "ROLE_PROGRAMMER"] },
  { path: "/admin/schedules",icon: <TrainIcon size={16}/>,    label: "Horaires / Trains",roles: ["ROLE_ADMIN", "ROLE_AGENT", "ROLE_PROGRAMMER"] },
  { path: "/admin/bookings", icon: <TicketIcon size={16}/>,   label: "Réservations",     roles: ["ROLE_ADMIN", "ROLE_AGENT", "ROLE_PROGRAMMER", "ROLE_CONTROLLER"] },
  { path: "/admin/scan",     icon: <CameraIcon size={16}/>,   label: "Contrôle billets", roles: ["ROLE_ADMIN", "ROLE_AGENT", "ROLE_CONTROLLER", "ROLE_PROGRAMMER"] },
  { path: "/admin/users",    icon: <UsersIcon size={16}/>,    label: "Voyageurs",        roles: ["ROLE_ADMIN", "ROLE_AGENT"] },
  { path: "/admin/revenue",  icon: <MoneyIcon size={16}/>,    label: "Recettes",         roles: ["ROLE_ADMIN"] },
  { path: "/admin/settings", icon: <SettingsIcon size={16}/>, label: "Paramètres",       roles: ["ROLE_ADMIN"] },
];

const ROLE_META = {
  ROLE_ADMIN:      { role: "ADMIN",      roleLabel: "Administrateur", roleIcon: <ShieldIcon size={15}/> },
  ROLE_AGENT:      { role: "AGENT",      roleLabel: "Agent Guichet",  roleIcon: <TicketIcon size={15}/> },
  ROLE_PROGRAMMER: { role: "PROG",       roleLabel: "Programmateur",  roleIcon: <TrainIcon size={15}/> },
  ROLE_CONTROLLER: { role: "CONTROLLER", roleLabel: "Contrôleur",     roleIcon: <CameraIcon size={15}/> },
  ROLE_SUPPORT:    { role: "SUPPORT",    roleLabel: "Support",        roleIcon: <MessageIcon size={15}/> },
};

/** Dérive le rôle courant (et ses libellés d'affichage) depuis le contexte d'auth. */
export function useStaffRole() {
  const { user } = useAuth();
  const roles = Array.isArray(user?.roles)
    ? user.roles.map(r => (typeof r === "string" ? r : r.authority ?? r.role ?? ""))
    : (user?.roles ? [user.roles] : []);

  const primary = ["ROLE_ADMIN", "ROLE_AGENT", "ROLE_PROGRAMMER", "ROLE_CONTROLLER", "ROLE_SUPPORT"].find(r => roles.includes(r));
  const meta = ROLE_META[primary] || { role: "STAFF", roleLabel: "Staff SETRAG", roleIcon: <UsersIcon size={15}/> };

  return { ...meta, roles, user, dashboardPath: DASHBOARD_PATH[primary] || "/admin" };
}

function RouteSidebar({ isOpen, onClose }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { logout } = useAuth();
  const { role, roleLabel, roleIcon, roles, user, dashboardPath } = useStaffRole();
  const { settings } = useSettings();
  const settingsLogoUrl = settings.logoUrl;
  const [hasNewAnnouncement, setHasNewAnnouncement] = useState(false);

  // Badge de notification sur "Messagerie" quand une annonce plus récente
  // que la dernière visite du mur a été publiée - pas besoin d'ouvrir la
  // messagerie pour savoir qu'il y a du nouveau.
  useEffect(() => {
    const check = async () => {
      try {
        const r = await api.get("/api/admin/announcements/latest");
        const latestAt = r.data?.latestAt;
        if (!latestAt) return;
        const seenAt = localStorage.getItem("wall_last_seen_at");
        setHasNewAnnouncement(!seenAt || new Date(latestAt) > new Date(seenAt));
      } catch {}
    };
    check();
    const interval = setInterval(check, 15000);
    return () => clearInterval(interval);
  }, []);

  const visibleNav = STAFF_NAV
    .filter(item => item.roles.some(r => roles.includes(r)))
    .map(item => item.path ? item : { ...item, path: dashboardPath });

  const handleNav = (path) => { navigate(path); onClose(); };

  return (
    <>
      {isOpen && <div className="sidebar-overlay" onClick={onClose}/>}
      <aside className={`sidebar${isOpen ? " open" : ""}`}>
        <div className="sb-logo">
          {settingsLogoUrl
            ? <img src={settingsLogoUrl} alt="SETRAG" style={{ height: 34, maxWidth: 90, objectFit: "contain" }} />
            : <div className="sb-logo-icon"><TrainIcon size={26}/></div>}
          <div><div className="sb-logo-name">SETRAG</div><div className="sb-logo-sub">{roleLabel}</div></div>
        </div>
        <div className="sb-nav">
          <div className="sb-section-lbl">Navigation</div>
          {visibleNav.map(item => (
            <button key={item.path + item.label} className={`sb-item${pathname === item.path ? " active" : ""}`} onClick={() => handleNav(item.path)} style={{ position: "relative" }}>
              <span className="sb-item-icon">{item.icon}</span>{item.label}
              {item.badge && hasNewAnnouncement && (
                <span style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", width: 8, height: 8, borderRadius: "50%", background: "#E0B030", boxShadow: "0 0 0 2px rgba(0,0,0,.25)" }} />
              )}
            </button>
          ))}
        </div>
        <div style={{ flex: 1 }} />
        <div className="sb-footer">
          <div className="sb-user">
            <div className="sb-avatar">{initials(user?.name)}</div>
            <div><div className="sb-uname">{user?.name || "-"}</div><div className="sb-urole">{roleIcon} {roleLabel}</div></div>
          </div>
          <button className="logout-btn" onClick={logout}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><LogoutIcon size={14}/> Déconnexion</span>
          </button>
        </div>
      </aside>
    </>
  );
}

/**
 * Coquille commune à TOUT l'espace staff (Admin, Agent, Programmateur,
 * Contrôleur, Support) : même police (Playfair Display + DM Sans), mêmes
 * couleurs, même sidebar avec les mêmes entrées de menu visibles selon le
 * rôle - utilisée par toutes les pages, qu'elles soient à onglets internes
 * (AdminPage, AgentPage, ProgrammerPage) ou de simples pages routées
 * (AdminUsers, AdminBookings, AdminSettings, ControllerScan, AdminMessages).
 */
export function StaffFrame({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { roleLabel, roleIcon } = useStaffRole();

  return (
    <div className="app">
      <style>{css}</style>

      <div className="mobile-topbar">
        <button className="hamburger" onClick={() => setSidebarOpen(o => !o)}>
          <span/><span/><span/>
        </button>
        <div style={{ textAlign: "center" }}>
          <div className="mobile-logo" style={{display:"flex",alignItems:"center",gap:6,justifyContent:"center"}}><TrainIcon size={16}/> SETRAG</div>
          <div className="mobile-role">{roleIcon} {roleLabel}</div>
        </div>
        <div style={{ width: 30 }} />
      </div>

      <div className="layout">
        <RouteSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <main className="content">{children}</main>
      </div>

      <footer style={{ background: T.forestDk, color: "rgba(255,255,255,.38)", textAlign: "center", padding: "14px", fontSize: 12, position: "relative", zIndex: 1 }}>
        <span style={{ color: T.goldLt, fontWeight: 700 }}>SETRAG</span>
        {" "}- Société d'Exploitation du Transgabonais &nbsp;|&nbsp; © 2025 &nbsp;·&nbsp; {roleIcon} {roleLabel}
      </footer>
    </div>
  );
}
